// ═══════════════════════════════════════════════════════════════════════
//   STUDENT WIZARD — Cadastro/Edição em 8 etapas
//   FASE 1: somente frontend, reaproveita /api/students (POST/PUT)
//   Rascunho em localStorage. Schema 100% intacto.
// ═══════════════════════════════════════════════════════════════════════
import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
  ChevronRight, ChevronLeft, CheckCircle2, AlertCircle, Save, X, Loader2,
  User as UserIcon, Users, Home, HeartPulse, FileText, GraduationCap, Wallet, ListChecks,
  History, Sparkles, Plus, Trash2, Search as SearchIcon, AlertTriangle, Link2, Copy,
  Edit2, Ban, RotateCcw, Undo2, Receipt, Phone, Mail,
} from 'lucide-react';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import * as VisuallyHidden from '@radix-ui/react-visually-hidden';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import GuardiansLinkedList, { formatApiError } from './GuardiansLinkedList';
import { useTituloActions } from '@/hooks/useTituloActions';

const API_URL = window.location.origin;

// ─── Etapas ─────────────────────────────────────────────────────────
const STEPS = [
  { id: 'aluno',       label: 'Dados do Aluno',     icon: UserIcon,       desc: 'Identificação' },
  { id: 'responsavel', label: 'Responsáveis',       icon: Users,          desc: 'Vínculos' },
  { id: 'endereco',    label: 'Contato & Endereço', icon: Home,           desc: 'Localização' },
  { id: 'saude',       label: 'Saúde & Segurança',  icon: HeartPulse,     desc: 'Cuidados' },
  { id: 'documentos',  label: 'Documentos',         icon: FileText,       desc: 'CPF/RG' },
  { id: 'pedagogico',  label: 'Pedagógico',         icon: GraduationCap,  desc: 'Histórico' },
  { id: 'financeiro',  label: 'Financeiro',         icon: Wallet,         desc: 'Plano' },
  { id: 'revisao',     label: 'Revisão Final',      icon: ListChecks,     desc: 'Confirmar' },
];

// ─── Máscaras ───────────────────────────────────────────────────────
export const maskCPF = v => (v || '').replace(/\D/g, '').slice(0, 11)
  .replace(/(\d{3})(\d)/, '$1.$2').replace(/(\d{3})(\d)/, '$1.$2').replace(/(\d{3})(\d{1,2})$/, '$1-$2');
export const maskPhone = v => {
  const d = (v || '').replace(/\D/g, '').slice(0, 11);
  if (d.length <= 10) return d.replace(/(\d{2})(\d{4})(\d{0,4}).*/, '($1) $2-$3').replace(/-$/, '');
  return d.replace(/(\d{2})(\d{5})(\d{0,4}).*/, '($1) $2-$3').replace(/-$/, '');
};
export const maskCEP = v => (v || '').replace(/\D/g, '').slice(0, 8).replace(/(\d{5})(\d{0,3}).*/, '$1-$2').replace(/-$/, '');

// ─── ViaCEP — busca endereço por CEP (auto-preenchimento) ───────────────
export async function fetchCEP(cep) {
  const digits = String(cep || '').replace(/\D/g, '');
  if (digits.length !== 8) return null;
  try {
    const res = await fetch(`https://viacep.com.br/ws/${digits}/json/`);
    if (!res.ok) return null;
    const data = await res.json();
    if (data?.erro) return null;
    return {
      address: data.logradouro || '',
      neighborhood: data.bairro || '',
      city: data.localidade || '',
      state: data.uf || '',
    };
  } catch { return null; }
}

export const calcAge = (birth) => {
  if (!birth) return null;
  const d = new Date(birth);
  if (isNaN(d)) return null;
  const today = new Date();
  let a = today.getFullYear() - d.getFullYear();
  const m = today.getMonth() - d.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < d.getDate())) a--;
  return a;
};

export const validEmail = e => !e || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e);

// E-mail institucional do aluno: primeiro_nome + DDMMAAAA + @novodna.com.br
export const EMAIL_DOMAIN = 'novodna.com.br';
export const institutionalEmail = (name, birthDate) => {
  const first = (name || '').trim().split(' ')[0] || '';
  const slug = first.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().replace(/[^a-z0-9]/g, '');
  const raw = (birthDate || '').toString().slice(0, 10);
  let d = '';
  if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) d = `${raw.slice(8, 10)}${raw.slice(5, 7)}${raw.slice(0, 4)}`;
  else if (/^\d{2}\/\d{2}\/\d{4}$/.test(raw)) d = raw.replace(/\//g, '');
  return slug && d ? `${slug}${d}@${EMAIL_DOMAIN}` : '';
};

// FASE 2 — Campos obrigatórios para Boleto Safra (replicado do AlunoFinanceiroTab)
export const REQUIRED_BANK_FIELDS = ['cpf','zip_code','address','address_number','neighborhood','city','state'];
export const isResponsavelIncomplete = (r) => !r || REQUIRED_BANK_FIELDS.some(k => !((r?.[k] || '').toString().trim()));
export const missingBankFields = (r) => REQUIRED_BANK_FIELDS.filter(k => !((r?.[k] || '').toString().trim()));
export const FIELD_LABELS_PT = { cpf:'CPF', zip_code:'CEP', address:'Endereço', address_number:'Número', neighborhood:'Bairro', city:'Cidade', state:'UF' };

// ─── Componente ─────────────────────────────────────────────────────
export default function StudentWizard({ open, onOpenChange, mode = 'new', student = null, classGroups = [], responsibles = [], onSaved, onResponsiblesUpdate }) {
  const draftKey = `student-wizard-draft-${mode}-${student?.id || 'new'}`;
  const [step, setStep] = useState(0);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});

  // Histórico anterior (FASE 1 enhancement) — busca por id (edit reativando) ou cpf/rg (new match)
  const [historico, setHistorico] = useState(null);   // { found, year, ... }
  const [historicoLoading, setHistoricoLoading] = useState(false);
  const [historicoDismissed, setHistoricoDismissed] = useState(false);

  // FASE 5 — Premium Absolute: prompts de rascunho (restaurar/descartar) e indicador discreto
  const [restorePrompt, setRestorePrompt] = useState({ open: false, draft: null });
  const [discardPrompt, setDiscardPrompt] = useState(false);
  const [lastSavedAt, setLastSavedAt] = useState(null);
  const [savedTick, setSavedTick] = useState(0); // refresh "há Xs"

  // Status que sinalizam ex-aluno → ao editar com esses status, sugere importar histórico
  const REACTIVATION_STATUSES = ['Inativo', 'Trancado', 'Transferido', 'Concluído'];

  const fetchHistorico = useCallback(async (params) => {
    if (!params || (!params.student_id && !params.cpf && !params.rg)) return;
    setHistoricoLoading(true);
    try {
      const merged = { ...params, with_ai: 'true' };
      const qs = new URLSearchParams(Object.entries(merged).filter(([, v]) => v)).toString();
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/students/historico-anterior?${qs}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) { setHistorico(null); return; }
      const data = await res.json();
      setHistorico(data && data.found ? data : null);
    } catch {
      setHistorico(null);
    } finally {
      setHistoricoLoading(false);
    }
  }, []);

  const applyHistorico = () => {
    if (!historico?.formatted_summary) return;
    const sep = '\n\n---\n';
    const current = (form.pedagogical_notes || '').trim();
    const merged = current ? `${current}${sep}${historico.formatted_summary}` : historico.formatted_summary;
    setForm(f => ({ ...f, pedagogical_notes: merged }));
    toast.success(`Histórico de ${historico.year} importado`);
    setHistoricoDismissed(true);
  };

  // Notifica o usuário quando match é encontrado em modo new (uma única vez por match)
  useEffect(() => {
    if (mode === 'new' && historico?.found && !historicoDismissed) {
      toast.info(`Cadastro existente: ${historico.name} • ${historico.class_name || 'sem turma'} (${historico.year}). Veja a etapa Pedagógico.`, { duration: 5000 });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [historico?.student_id]);

  // Inicializa form com student (edit) ou rascunho local (new)
  useEffect(() => {
    if (!open) return;
    if (mode === 'edit' && student) {
      setForm({ ...student });
    } else {
      try {
        const draft = localStorage.getItem(draftKey);
        if (draft) {
          // Premium: prompt modal "Você tem um rascunho salvo. Deseja continuar?"
          const parsed = JSON.parse(draft);
          setRestorePrompt({ open: true, draft: parsed });
          // Mantém form vazio até decisão do usuário
          setForm({ situation: 'Matriculado', nationality: 'Brasileiro(a)', is_foreigner: false, active: true });
        } else {
          setForm({ situation: 'Matriculado', nationality: 'Brasileiro(a)', is_foreigner: false, active: true });
        }
      } catch {
        setForm({ situation: 'Matriculado', nationality: 'Brasileiro(a)', is_foreigner: false, active: true });
      }
    }
    setStep(0);
    setErrors({});
    setLastSavedAt(null);
  }, [open, mode, student, draftKey]);

  const set = useCallback((k, v) => setForm(f => ({ ...f, [k]: v })), []);

  // Auto-save rascunho com índice de metadados (apenas modo new) — FASE 4
  useEffect(() => {
    if (!open || mode === 'edit') return;
    if (restorePrompt.open) return; // não sobrescreve rascunho enquanto usuário decide
    const id = setTimeout(() => {
      try {
        localStorage.setItem(draftKey, JSON.stringify(form));
        // Índice de rascunhos para listagem (last 10)
        const indexKey = 'student-wizard-drafts-index';
        const idxRaw = localStorage.getItem(indexKey);
        const idx = idxRaw ? JSON.parse(idxRaw) : [];
        const filtered = idx.filter(it => it.key !== draftKey);
        filtered.unshift({
          key: draftKey,
          name: form?.name || 'Sem nome',
          step: STEPS[step]?.label || '',
          updated_at: new Date().toISOString(),
        });
        localStorage.setItem(indexKey, JSON.stringify(filtered.slice(0, 10)));
        setLastSavedAt(Date.now());
      } catch { /* noop */ }
    }, 500);
    return () => clearTimeout(id);
  }, [form, open, mode, draftKey, step, restorePrompt.open]);

  // Tick para atualizar texto "há Xs" do indicador discreto
  useEffect(() => {
    if (!open || mode === 'edit' || !lastSavedAt) return;
    const id = setInterval(() => setSavedTick(t => t + 1), 5000);
    return () => clearInterval(id);
  }, [open, mode, lastSavedAt]);

  // Reset histórico ao abrir/trocar aluno
  useEffect(() => {
    if (!open) return;
    setHistorico(null);
    setHistoricoDismissed(false);
  }, [open, mode, student?.id]);

  // EDIT: reativação → ano anterior do registro atual
  useEffect(() => {
    if (!open || mode !== 'edit' || !student?.id) return;
    if (!REACTIVATION_STATUSES.includes(student.situation)) return;
    const baseYear = student.year || new Date().getFullYear();
    fetchHistorico({ student_id: student.id, year: baseYear - 1 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, mode, student?.id, student?.situation, student?.year]);

  // NEW: CPF/RG match (debounce simples) — busca quando documentos completos
  useEffect(() => {
    if (!open || mode !== 'new' || historicoDismissed) return;
    const cpfDigits = (form.cpf || '').replace(/\D/g, '');
    const rg = (form.rg || '').trim();
    if (cpfDigits.length < 11 && rg.length < 5) return;
    const id = setTimeout(() => {
      const yearTarget = (form.year ? Number(form.year) : new Date().getFullYear()) - 1;
      fetchHistorico({
        cpf: cpfDigits.length === 11 ? cpfDigits : undefined,
        rg: rg.length >= 5 ? rg : undefined,
        year: yearTarget,
      });
    }, 600);
    return () => clearTimeout(id);
  }, [form.cpf, form.rg, form.year, open, mode, historicoDismissed, fetchHistorico]);

  // Idade calculada
  const idade = useMemo(() => calcAge(form.birth_date), [form.birth_date]);

  // Validação por etapa
  const stepErrors = useMemo(() => {
    const e = {};
    if (step === 0) {
      if (!form.name?.trim()) e.name = 'Nome obrigatório';
    }
    if (step === 1) {
      if (!form.responsible_id) e.responsible_id = 'Responsável financeiro obrigatório';
    }
    if (step === 2) {
      if (form.email && !validEmail(form.email)) e.email = 'E-mail inválido';
    }
    return e;
  }, [step, form]);

  const canNext = Object.keys(stepErrors).length === 0;

  // FASE 5 — Score de completude do checklist em tempo real (header)
  const liveChecklist = useMemo(() => {
    const respFin = responsibles.find(r => r.id === form.responsible_id);
    return buildWizardChecklist(form, respFin, classGroups);
  }, [form, responsibles, classGroups]);
  const checklistOk = liveChecklist.filter(i => i.ok && !i.warn && !i.fail).length;
  const checklistTotal = liveChecklist.length;
  const checklistFail = liveChecklist.filter(i => i.fail).length;

  const goNext = () => {
    if (!canNext) { setErrors(stepErrors); return; }
    setStep(s => Math.min(s + 1, STEPS.length - 1));
    setErrors({});
  };
  const goBack = () => setStep(s => Math.max(s - 1, 0));

  // Limpa rascunho e fecha o wizard (uso interno após confirmação)
  const clearDraftAndClose = useCallback(() => {
    try {
      localStorage.removeItem(draftKey);
      const indexKey = 'student-wizard-drafts-index';
      const idxRaw = localStorage.getItem(indexKey);
      if (idxRaw) {
        const idx = JSON.parse(idxRaw).filter(it => it.key !== draftKey);
        localStorage.setItem(indexKey, JSON.stringify(idx));
      }
    } catch { /* noop */ }
    onOpenChange(false);
  }, [draftKey, onOpenChange]);

  // Detecta se há conteúdo significativo no rascunho (evita prompt em form vazio)
  const hasMeaningfulDraft = useCallback(() => {
    if (mode !== 'new') return false;
    const ignored = new Set(['situation', 'nationality', 'is_foreigner', 'active']);
    return Object.entries(form || {}).some(([k, v]) => {
      if (ignored.has(k)) return false;
      if (v === null || v === undefined || v === '' || v === false) return false;
      if (Array.isArray(v) && v.length === 0) return false;
      return true;
    });
  }, [form, mode]);

  const handleCancel = () => {
    if (mode === 'new' && hasMeaningfulDraft()) {
      setDiscardPrompt(true);
      return;
    }
    if (mode === 'new') {
      // form vazio — sem prompt, apenas limpa qualquer rascunho residual e fecha
      clearDraftAndClose();
      return;
    }
    onOpenChange(false);
  };

  const handleSubmit = async () => {
    // Validação final mínima
    if (!form.name?.trim()) { setStep(0); setErrors({ name: 'Nome obrigatório' }); return; }
    if (!form.responsible_id) { setStep(1); setErrors({ responsible_id: 'Responsável financeiro obrigatório' }); return; }

    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const headers = { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };
      const cg = form.class_group_id ? classGroups.find(c => c.id === form.class_group_id) : null;
      const payload = {
        ...form,
        // Campos obrigatórios pelo backend (StudentCreate) — garante presença mesmo se em branco
        registration_number: form.registration_number || '',
        class_name: form.class_name || (cg ? cg.name : ''),
        // saneia para o schema do backend (Student)
        active: form.active !== false,
        is_foreigner: !!form.is_foreigner,
        no_email: !!form.no_email,
        no_sms: !!form.no_sms,
        year: form.year ? Number(form.year) : new Date().getFullYear(),
      };
      const url = mode === 'edit'
        ? `${API_URL}/api/students/${student.id}`
        : `${API_URL}/api/students`;
      const method = mode === 'edit' ? 'PUT' : 'POST';
      const res = await fetch(url, { method, headers, body: JSON.stringify(payload) });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(typeof err.detail === 'string' ? err.detail : 'Erro ao salvar');
      }
      const saved = await res.json();
      // Aplicar endereço do aluno aos responsáveis sem endereço completo (regra opcional)
      if (form.apply_aluno_address_to_responsibles) {
        try {
          const REQ = ['zip_code','address','address_number','neighborhood','city','state'];
          const studentHas = REQ.every(k => (form[k] || '').toString().trim());
          if (studentHas) {
            const targetIds = [form.responsible_id, form.responsible_didactic_id, form.responsible_financial_optional_id]
              .filter(Boolean);
            const seen = new Set();
            for (const rid of targetIds) {
              if (seen.has(rid)) continue;
              seen.add(rid);
              const r = (responsibles || []).find(x => x.id === rid);
              if (!r) continue;
              const incomplete = REQ.some(k => !((r[k] || '').toString().trim()));
              if (!incomplete) continue;
              const payload = {
                ...r,
                zip_code: r.zip_code || form.zip_code,
                address: r.address || form.address,
                address_number: r.address_number || form.address_number,
                complement: r.complement || form.complement,
                neighborhood: r.neighborhood || form.neighborhood,
                city: r.city || form.city,
                state: r.state || form.state,
              };
              await fetch(`${API_URL}/api/responsibles/${rid}`, {
                method: 'PUT',
                headers,
                body: JSON.stringify(payload),
              }).catch(() => {});
            }
          }
        } catch { /* não bloqueia salvamento do aluno */ }
      }
      toast.success(mode === 'edit' ? 'Aluno atualizado' : 'Aluno cadastrado');
      try {
        localStorage.removeItem(draftKey);
        const indexKey = 'student-wizard-drafts-index';
        const idxRaw = localStorage.getItem(indexKey);
        if (idxRaw) {
          const idx = JSON.parse(idxRaw).filter(it => it.key !== draftKey);
          localStorage.setItem(indexKey, JSON.stringify(idx));
        }
      } catch { /* noop */ }
      onSaved?.(saved);
      onOpenChange(false);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSaving(false);
    }
  };

  // Helper UI (Field é definido FORA do componente, ver no fim do arquivo, evita perda de foco)

  // ─── Render por etapa ────────────────────────────────────────────
  const renderStep = () => {
    switch (STEPS[step].id) {
      case 'aluno':
        return (
          <div className="grid grid-cols-2 gap-3">
            <Field label="Nome completo *" error={errors.name} full>
              <Input value={form.name || ''} onChange={e => set('name', e.target.value)} data-testid="wiz-name" />
            </Field>
            <Field label="Matrícula"><Input value={form.registration_number || ''} onChange={e => set('registration_number', e.target.value)} /></Field>
            <Field label="Turma">
              <Select value={form.class_group_id || ''} onValueChange={v => set('class_group_id', v)}>
                <SelectTrigger><SelectValue placeholder="Selecione" /></SelectTrigger>
                <SelectContent>{classGroups.map(cg => <SelectItem key={cg.id} value={cg.id}>{cg.name}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Status">
              <Select value={form.situation || 'Matriculado'} onValueChange={v => set('situation', v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {['Matriculado','Ativo','Inativo','Trancado','Transferido','Concluído'].map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Data de nascimento" hint={idade != null ? `${idade} anos` : null}>
              <Input type="date" value={form.birth_date || ''} onChange={e => set('birth_date', e.target.value)} />
            </Field>
            <Field label="Gênero">
              <Select value={form.gender || ''} onValueChange={v => set('gender', v)}>
                <SelectTrigger><SelectValue placeholder="Selecione" /></SelectTrigger>
                <SelectContent>{['Feminino','Masculino','Outro','Prefere não informar'].map(g => <SelectItem key={g} value={g}>{g}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Nacionalidade"><Input value={form.nationality || ''} onChange={e => set('nationality', e.target.value)} /></Field>
            <Field label="Naturalidade"><Input value={form.birth_city || ''} onChange={e => set('birth_city', e.target.value)} /></Field>
            <Field label="Observações gerais" full>
              <Textarea value={form.notes || ''} onChange={e => set('notes', e.target.value)} rows={3} />
            </Field>
          </div>
        );

      case 'responsavel':
        return (
          <ResponsaveisStep
            form={form}
            set={set}
            errors={errors}
            responsibles={responsibles}
            onResponsiblesUpdate={onResponsiblesUpdate}
          />
        );

      case 'endereco':
        return (
          <div className="grid grid-cols-2 gap-3">
            <Field label="CEP">
              <Input
                value={form.zip_code || ''}
                onChange={e => set('zip_code', maskCEP(e.target.value))}
                onBlur={async (e) => {
                  const data = await fetchCEP(e.target.value);
                  if (!data) return;
                  if (!form.address) set('address', data.address);
                  if (!form.neighborhood) set('neighborhood', data.neighborhood);
                  if (!form.city) set('city', data.city);
                  if (!form.state) set('state', data.state);
                }}
                placeholder="00000-000"
                data-testid="wiz-aluno-cep"
              />
            </Field>
            <Field label="Endereço"><Input value={form.address || ''} onChange={e => set('address', e.target.value)} /></Field>
            <Field label="Número"><Input value={form.address_number || ''} onChange={e => set('address_number', e.target.value)} /></Field>
            <Field label="Complemento"><Input value={form.complement || ''} onChange={e => set('complement', e.target.value)} /></Field>
            <Field label="Bairro"><Input value={form.neighborhood || ''} onChange={e => set('neighborhood', e.target.value)} /></Field>
            <Field label="Cidade"><Input value={form.city || ''} onChange={e => set('city', e.target.value)} /></Field>
            <Field label="Estado"><Input value={form.state || ''} maxLength={2} onChange={e => set('state', e.target.value.toUpperCase())} /></Field>
            <div />
            <Field label="Telefone principal"><Input value={form.phone || ''} onChange={e => set('phone', maskPhone(e.target.value))} /></Field>
            <Field label="Celular / WhatsApp"><Input value={form.cellphone || ''} onChange={e => set('cellphone', maskPhone(e.target.value))} /></Field>
            <Field label="E-mail" error={errors.email} full><Input type="email" value={form.email || ''} onChange={e => set('email', e.target.value)} /></Field>
          </div>
        );

      case 'saude':
        return (
          <div className="grid grid-cols-2 gap-3">
            <Field label="Tipo sanguíneo">
              <Select value={form.blood_type || ''} onValueChange={v => set('blood_type', v)}>
                <SelectTrigger><SelectValue placeholder="Selecione" /></SelectTrigger>
                <SelectContent>{['A+','A-','B+','B-','AB+','AB-','O+','O-'].map(b => <SelectItem key={b} value={b}>{b}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Deficiência / necessidade específica"><Input value={form.disabilities || ''} onChange={e => set('disabilities', e.target.value)} /></Field>
            <Field label="Alergias" full><Textarea value={form.allergies || ''} onChange={e => set('allergies', e.target.value)} rows={2} /></Field>
            <Field label="Doenças / condições" full><Textarea value={form.diseases || ''} onChange={e => set('diseases', e.target.value)} rows={2} /></Field>
            <Field label="Notas de primeiros socorros / medicações" full><Textarea value={form.first_aid_notes || ''} onChange={e => set('first_aid_notes', e.target.value)} rows={2} /></Field>
          </div>
        );

      case 'documentos':
        return (
          <div className="grid grid-cols-2 gap-3">
            <Field label="CPF"><Input value={form.cpf || ''} onChange={e => set('cpf', maskCPF(e.target.value))} placeholder="000.000.000-00" /></Field>
            <Field label="RG"><Input value={form.rg || ''} onChange={e => set('rg', e.target.value)} /></Field>
            <Field label="Órgão emissor"><Input value={form.rg_orgao || ''} onChange={e => set('rg_orgao', e.target.value)} /></Field>
            <Field label="Data de emissão (RG)"><Input type="date" value={form.rg_data_exp || ''} onChange={e => set('rg_data_exp', e.target.value)} /></Field>
            <Field label="RA (registro escolar)"><Input value={form.ra || ''} onChange={e => set('ra', e.target.value)} /></Field>
            <p className="col-span-2 text-[11px] text-slate-400 pt-1">📎 Upload de documentos virá em fase futura.</p>
          </div>
        );

      case 'pedagogico':
        return (
          <div className="space-y-3">
            <HistoricoCard
              data={historico}
              loading={historicoLoading}
              dismissed={historicoDismissed}
              hasNotes={!!(form.pedagogical_notes || '').trim()}
              onApply={applyHistorico}
              onDismiss={() => setHistoricoDismissed(true)}
              mode={mode}
            />
            <div className="grid grid-cols-2 gap-3">
              <Field label="Classificação"><Input value={form.classification || ''} onChange={e => set('classification', e.target.value)} placeholder="Ex.: Bolsista, Conveniado..." /></Field>
              <Field label="Número de chamada"><Input type="number" value={form.call_number || ''} onChange={e => set('call_number', e.target.value ? Number(e.target.value) : null)} /></Field>
              <Field label="Permanência — entrada"><Input type="time" value={form.permanence_entry || ''} onChange={e => set('permanence_entry', e.target.value)} /></Field>
              <Field label="Permanência — saída"><Input type="time" value={form.permanence_exit || ''} onChange={e => set('permanence_exit', e.target.value)} /></Field>
              <Field label="Observações pedagógicas" full>
                <Textarea value={form.pedagogical_notes || ''} onChange={e => set('pedagogical_notes', e.target.value)} rows={5} placeholder="Histórico, escola anterior, autorizações..." data-testid="wiz-pedagogical-notes" />
              </Field>
            </div>
          </div>
        );

      case 'financeiro':
        return (
          <FinanceiroStep
            form={form}
            set={set}
            classGroups={classGroups}
            mode={mode}
            studentId={student?.id}
          />
        );

      case 'revisao': {
        const respFinSel = responsibles.find(r => r.id === form.responsible_id);
        const respPedSel = responsibles.find(r => r.id === form.responsible_didactic_id);
        const finIncomplete = respFinSel && isResponsavelIncomplete(respFinSel);
        const pickups = Array.isArray(form.authorized_pickups) ? form.authorized_pickups : [];
        const checklist = buildWizardChecklist(form, respFinSel, classGroups);
        return (
          <div className="space-y-3">
            {/* FASE 4 — Checklist de pendências */}
            <ChecklistCard items={checklist} onJump={(stepIdx) => setStep(stepIdx)} />

            <p className="text-sm font-semibold text-slate-700 mb-2 mt-1">Revise antes de finalizar:</p>
            <ReviewLine label="Nome" value={form.name} required />
            <ReviewLine label="Matrícula" value={form.registration_number} />
            <ReviewLine label="Turma" value={classGroups.find(c => c.id === form.class_group_id)?.name} />
            <ReviewLine label="Status" value={form.situation} />
            <ReviewLine label="Nascimento" value={form.birth_date ? `${form.birth_date} (${idade} anos)` : null} />
            <ReviewLine label="Responsável Fin." value={respFinSel?.name} required />
            {finIncomplete && (
              <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 flex items-start gap-2 my-2" data-testid="wiz-review-incomplete-alert">
                <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
                <div className="text-[12px] text-amber-900 leading-snug">
                  <strong>Responsável Financeiro com cadastro incompleto.</strong> Faltam: {missingBankFields(respFinSel).map(k => FIELD_LABELS_PT[k] || k).join(', ')}. Boleto Safra ficará bloqueado até completar.
                </div>
              </div>
            )}
            <ReviewLine label="Responsável Ped." value={respPedSel?.name || (form.responsible_didactic_id === form.responsible_id ? respFinSel?.name + ' (mesmo do financeiro)' : null)} />
            <ReviewLine label="Autorizados a retirar" value={pickups.length > 0 ? pickups.map(p => p.name).filter(Boolean).join(', ') : (form.authorized_pickup || null)} />
            <ReviewLine label="Email" value={form.email} />
            <ReviewLine label="Telefone" value={form.phone || form.cellphone} />
            <ReviewLine label="Endereço" value={form.address ? `${form.address}, ${form.address_number || 's/n'} - ${form.city || ''}/${form.state || ''}` : null} />
            <ReviewLine label="CPF" value={form.cpf} />
            <ReviewLine label="Tipo sanguíneo" value={form.blood_type} />
            <ReviewLine label="Planos financeiros" value={(form.financial_plans || []).length > 0 ? `${(form.financial_plans || []).length} plano(s) vinculado(s)` : null} />
          </div>
        );
      }

      default: return null;
    }
  };

  const progress = ((step + 1) / STEPS.length) * 100;
  const StepIcon = STEPS[step].icon;

  // FASE 4 — Drafts panel
  const [showDrafts, setShowDrafts] = useState(false);
  const [draftsList, setDraftsList] = useState([]);
  const refreshDraftsList = useCallback(() => {
    try {
      const raw = localStorage.getItem('student-wizard-drafts-index');
      const idx = raw ? JSON.parse(raw) : [];
      // Filtra rascunhos do modo new (chave começa com 'student-wizard-draft-new-') e exclui o atual
      const list = idx.filter(it => typeof it.key === 'string' && it.key.startsWith('student-wizard-draft-new-') && it.key !== draftKey);
      setDraftsList(list);
    } catch { setDraftsList([]); }
  }, [draftKey]);
  useEffect(() => { if (open && mode === 'new') refreshDraftsList(); }, [open, mode, refreshDraftsList, form]);
  const restoreDraft = (key) => {
    try {
      const raw = localStorage.getItem(key);
      if (raw) {
        setForm(JSON.parse(raw));
        setStep(0);
        setShowDrafts(false);
        toast.success('Rascunho carregado');
      }
    } catch { toast.error('Erro ao carregar rascunho'); }
  };
  const deleteDraft = (key) => {
    try {
      localStorage.removeItem(key);
      const indexKey = 'student-wizard-drafts-index';
      const idxRaw = localStorage.getItem(indexKey);
      if (idxRaw) {
        const idx = JSON.parse(idxRaw).filter(it => it.key !== key);
        localStorage.setItem(indexKey, JSON.stringify(idx));
      }
      refreshDraftsList();
      toast.success('Rascunho descartado');
    } catch { /* noop */ }
  };

  return (
    <>
    <Dialog open={open} onOpenChange={(o) => !o ? handleCancel() : onOpenChange(o)}>
      <DialogContent className="max-w-4xl w-[96vw] max-h-[94vh] overflow-hidden p-0 rounded-2xl shadow-2xl border border-violet-100/40" data-testid="student-wizard">
        <VisuallyHidden.Root>
          <DialogTitle>{mode === 'edit' ? 'Editar Aluno' : 'Cadastro de Aluno'} — {STEPS[step].label}</DialogTitle>
        </VisuallyHidden.Root>
        {/* Header — premium DNACore (violeta + verde) */}
        <div className="relative bg-gradient-to-br from-violet-700 via-violet-600 to-fuchsia-700 text-white px-6 sm:px-8 pt-5 pb-4 overflow-hidden">
          {/* Glow decorativo */}
          <div className="pointer-events-none absolute -bottom-16 -left-12 w-48 h-48 rounded-full bg-violet-400/30 blur-3xl" />
          <div className="relative flex items-center justify-between">
            <div className="flex items-center gap-3.5">
              <div className="w-12 h-12 rounded-2xl bg-white/15 backdrop-blur-sm flex items-center justify-center ring-1 ring-white/20 shadow-lg">
                <StepIcon className="w-6 h-6" />
              </div>
              <div>
                <p className="text-[10.5px] uppercase tracking-[0.18em] text-violet-100/90 font-semibold">
                  {mode === 'edit' ? 'Editar cadastro' : 'Novo aluno'} · Etapa {step + 1} de {STEPS.length}
                </p>
                <h2 className="text-xl font-bold leading-tight">{STEPS[step].label}</h2>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {/* FASE 5 — Score de completude do checklist em tempo real */}
              <div
                className={`hidden md:flex flex-col items-end px-3 py-1.5 rounded-xl border backdrop-blur-sm transition-colors ${
                  checklistFail > 0
                    ? 'bg-rose-500/15 border-rose-300/40'
                    : checklistOk === checklistTotal
                      ? 'bg-emerald-400/20 border-emerald-300/40'
                      : 'bg-white/10 border-white/15'
                }`}
                title={`${checklistOk} de ${checklistTotal} itens completos`}
                data-testid="wiz-header-score"
              >
                <span className="text-[9px] uppercase tracking-[0.18em] text-violet-100/80 font-semibold leading-none">
                  Cadastro
                </span>
                <span className="text-sm font-bold leading-tight">
                  {checklistOk}/{checklistTotal}
                  {checklistFail > 0 && (
                    <span className="ml-1 text-[10px] font-semibold text-rose-100">
                      · {checklistFail} crítico{checklistFail > 1 ? 's' : ''}
                    </span>
                  )}
                </span>
              </div>
              <button onClick={handleCancel} className="p-2 rounded-xl hover:bg-white/15 transition-colors" aria-label="Fechar">
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>
          {/* FASE 4 — Botão Rascunhos (apenas mode=new) */}
          {mode === 'new' && draftsList.length > 0 && (
            <button
              onClick={() => setShowDrafts(s => !s)}
              className="absolute top-4 right-14 text-[10px] uppercase tracking-wider px-2.5 py-1 rounded-md bg-white/15 hover:bg-white/25 text-white font-bold flex items-center gap-1 transition-colors"
              data-testid="wiz-drafts-toggle"
            >
              <Save className="w-3 h-3" />
              {draftsList.length} rascunho{draftsList.length > 1 ? 's' : ''}
            </button>
          )}
          {/* Progress bar elegante c/ glow */}
          <div className="relative mt-5 h-2 rounded-full bg-white/15 overflow-hidden ring-1 ring-white/10">
            <div className="absolute inset-y-0 left-0 bg-gradient-to-r from-emerald-300 via-emerald-400 to-teal-400 shadow-[0_0_18px_rgba(16,185,129,.55)] transition-all duration-500"
                 style={{ width: `${progress}%` }} />
          </div>
          {/* Stepper c/ ícones */}
          <div className="relative mt-3 flex items-center gap-1.5 overflow-x-auto pb-1 -mx-1 px-1 scrollbar-thin">
            {STEPS.map((s, i) => {
              const Icon = s.icon;
              const active = i === step;
              const done = i < step;
              return (
                <button
                  key={s.id}
                  onClick={() => i < step && setStep(i)}
                  className={`shrink-0 text-[10.5px] uppercase tracking-wider px-2.5 py-1.5 rounded-lg transition-all flex items-center gap-1.5 ${
                    active ? 'bg-white text-violet-700 font-bold shadow-md' :
                    done ? 'text-emerald-200 hover:text-white cursor-pointer hover:bg-white/10' :
                    'text-violet-200/70 cursor-default'
                  }`}
                  data-testid={`wiz-step-${s.id}`}
                >
                  {done ? <CheckCircle2 className="w-3 h-3" /> : <Icon className="w-3 h-3" />}
                  <span className="hidden sm:inline">{s.label}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* FASE 4 — Painel de Rascunhos */}
        {mode === 'new' && showDrafts && (
          <div className="border-b border-violet-100 bg-violet-50/40 px-6 py-3 max-h-40 overflow-y-auto" data-testid="wiz-drafts-panel">
            <div className="flex items-center justify-between mb-2">
              <p className="text-[11px] uppercase tracking-wider font-bold text-violet-700">Rascunhos salvos ({draftsList.length})</p>
              <button onClick={() => setShowDrafts(false)} className="text-[11px] text-slate-500 hover:text-slate-700">Fechar</button>
            </div>
            <div className="space-y-1.5">
              {draftsList.map((d) => (
                <div key={d.key} className="flex items-center gap-2 bg-white border border-violet-100 rounded-md px-2.5 py-1.5" data-testid={`wiz-draft-${d.key}`}>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-semibold text-slate-700 truncate">{d.name}</p>
                    <p className="text-[10px] text-slate-400">
                      {d.step} · {new Date(d.updated_at).toLocaleString('pt-BR', { timeZone: 'America/Sao_Paulo' })}
                    </p>
                  </div>
                  <button onClick={() => restoreDraft(d.key)} className="text-[10.5px] px-2 py-0.5 rounded bg-violet-600 hover:bg-violet-700 text-white font-semibold" data-testid={`wiz-draft-restore-${d.key}`}>
                    Carregar
                  </button>
                  <button onClick={() => deleteDraft(d.key)} className="p-1 rounded hover:bg-rose-50" data-testid={`wiz-draft-delete-${d.key}`}>
                    <Trash2 className="w-3.5 h-3.5 text-rose-500" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Body — mais respiro, scroll suave */}
        <div className="px-6 sm:px-8 py-6 overflow-y-auto bg-gradient-to-b from-white to-slate-50/40" style={{ maxHeight: 'calc(94vh - 290px)' }}>
          {renderStep()}
        </div>

        {/* Footer — sticky, gradient sutil */}
        <div className="border-t border-slate-200/70 px-6 sm:px-8 py-3.5 flex items-center justify-between bg-white/80 backdrop-blur-sm shadow-[0_-4px_12px_rgba(0,0,0,0.04)]">
          <div className="flex items-center gap-3">
            <Button variant="ghost" onClick={handleCancel} disabled={saving} data-testid="wiz-cancel" className="text-slate-500 hover:text-slate-700">
              <X className="w-4 h-4 mr-1" /> Cancelar
            </Button>
            {/* Indicador discreto "Rascunho salvo" */}
            {mode === 'new' && lastSavedAt && !restorePrompt.open && (
              <span
                className="hidden sm:flex items-center gap-1 text-[10.5px] text-emerald-700 bg-emerald-50 border border-emerald-100 rounded-full px-2 py-0.5"
                data-testid="wiz-draft-indicator"
                key={savedTick}
              >
                <CheckCircle2 className="w-3 h-3" />
                Rascunho salvo {(() => {
                  const sec = Math.max(0, Math.floor((Date.now() - lastSavedAt) / 1000));
                  if (sec < 5) return 'agora';
                  if (sec < 60) return `há ${sec}s`;
                  const m = Math.floor(sec / 60);
                  return `há ${m}min`;
                })()}
              </span>
            )}
          </div>
          <div className="flex gap-2">
            {step > 0 && (
              <Button variant="outline" onClick={goBack} disabled={saving} data-testid="wiz-back" className="border-slate-200">
                <ChevronLeft className="w-4 h-4 mr-1" /> Voltar
              </Button>
            )}
            {mode === 'new' && (
              <Button variant="ghost" size="sm" disabled={saving} className="text-[11px] text-violet-600 hover:text-violet-800"
                onClick={() => { try { localStorage.setItem(draftKey, JSON.stringify(form)); toast.success('Rascunho salvo'); } catch { /* noop */ } }}>
                <Save className="w-3.5 h-3.5 mr-1" /> Salvar rascunho
              </Button>
            )}
            {step < STEPS.length - 1 ? (
              <Button onClick={goNext} disabled={saving} className="bg-gradient-to-r from-violet-600 to-violet-700 hover:from-violet-700 hover:to-violet-800 shadow-md shadow-violet-500/20" data-testid="wiz-next">
                Próximo <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            ) : (
              <Button onClick={handleSubmit} disabled={saving} className="bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 shadow-md shadow-emerald-500/25" data-testid="wiz-finish">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><CheckCircle2 className="w-4 h-4 mr-1" /> Finalizar cadastro</>}
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>

    {/* PROMPT — Restaurar rascunho ao reabrir */}
    <Dialog
      open={restorePrompt.open}
      onOpenChange={(o) => { if (!o) setRestorePrompt({ open: false, draft: null }); }}
    >
      <DialogContent className="max-w-md p-0 overflow-hidden rounded-2xl border border-violet-200 shadow-2xl" data-testid="wiz-restore-prompt">
        <VisuallyHidden.Root><DialogTitle>Rascunho salvo encontrado</DialogTitle></VisuallyHidden.Root>
        <div className="bg-gradient-to-br from-violet-700 via-violet-600 to-fuchsia-700 text-white px-5 py-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-white/15 backdrop-blur-sm flex items-center justify-center ring-1 ring-white/20">
              <Save className="w-5 h-5" />
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-wider text-violet-100/90 font-semibold">Continuar de onde parou</p>
              <h3 className="text-lg font-bold leading-tight">Você tem um rascunho salvo</h3>
            </div>
          </div>
        </div>
        <div className="px-5 py-4 bg-white">
          <p className="text-sm text-slate-700 leading-relaxed">
            Encontramos um cadastro em andamento{restorePrompt.draft?.name ? <> de <strong className="text-violet-700">{restorePrompt.draft.name}</strong></> : ''}. Deseja continuar de onde parou ou começar um novo cadastro?
          </p>
          <div className="mt-4 flex flex-col sm:flex-row gap-2 sm:justify-end">
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                try {
                  localStorage.removeItem(draftKey);
                  const indexKey = 'student-wizard-drafts-index';
                  const idxRaw = localStorage.getItem(indexKey);
                  if (idxRaw) {
                    const idx = JSON.parse(idxRaw).filter(it => it.key !== draftKey);
                    localStorage.setItem(indexKey, JSON.stringify(idx));
                  }
                } catch { /* noop */ }
                setForm({ situation: 'Matriculado', nationality: 'Brasileiro(a)', is_foreigner: false, active: true });
                setStep(0);
                setLastSavedAt(null);
                setRestorePrompt({ open: false, draft: null });
                toast.success('Rascunho descartado');
              }}
              className="border-rose-200 text-rose-700 hover:bg-rose-50"
              data-testid="wiz-restore-discard"
            >
              <Trash2 className="w-3.5 h-3.5 mr-1" /> Descartar
            </Button>
            <Button
              size="sm"
              onClick={() => {
                if (restorePrompt.draft) setForm(restorePrompt.draft);
                setStep(0);
                setRestorePrompt({ open: false, draft: null });
                toast.success('Rascunho restaurado');
              }}
              className="bg-gradient-to-r from-violet-600 to-violet-700 hover:from-violet-700 hover:to-violet-800"
              data-testid="wiz-restore-continue"
            >
              <Undo2 className="w-3.5 h-3.5 mr-1" /> Continuar rascunho
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>

    {/* PROMPT — Confirmar descarte ao Cancelar */}
    <Dialog open={discardPrompt} onOpenChange={(o) => { if (!o) setDiscardPrompt(false); }}>
      <DialogContent className="max-w-md p-0 overflow-hidden rounded-2xl border border-rose-200 shadow-2xl" data-testid="wiz-discard-prompt">
        <VisuallyHidden.Root><DialogTitle>Descartar rascunho</DialogTitle></VisuallyHidden.Root>
        <div className="bg-gradient-to-br from-rose-600 to-rose-500 text-white px-5 py-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-white/15 backdrop-blur-sm flex items-center justify-center ring-1 ring-white/20">
              <AlertTriangle className="w-5 h-5" />
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-wider text-rose-100 font-semibold">Confirmação necessária</p>
              <h3 className="text-lg font-bold leading-tight">Deseja descartar o rascunho?</h3>
            </div>
          </div>
        </div>
        <div className="px-5 py-4 bg-white">
          <p className="text-sm text-slate-700 leading-relaxed">
            Você tem um cadastro em andamento. Se continuar, todas as informações preenchidas serão perdidas. Esta ação não pode ser desfeita.
          </p>
          <div className="mt-4 flex flex-col sm:flex-row gap-2 sm:justify-end">
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                // Salva o rascunho atual (caso ainda não tenha sido) e mantém a janela aberta
                try { localStorage.setItem(draftKey, JSON.stringify(form)); } catch { /* noop */ }
                setDiscardPrompt(false);
                onOpenChange(false);
                toast.success('Rascunho mantido — você pode continuar depois');
              }}
              data-testid="wiz-discard-keep"
            >
              <Save className="w-3.5 h-3.5 mr-1" /> Manter rascunho
            </Button>
            <Button
              size="sm"
              onClick={() => {
                setDiscardPrompt(false);
                clearDraftAndClose();
                toast.success('Rascunho descartado');
              }}
              className="bg-rose-600 hover:bg-rose-700"
              data-testid="wiz-discard-confirm"
            >
              <Trash2 className="w-3.5 h-3.5 mr-1" /> Descartar e sair
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
    </>
  );
}

const ReviewLine = ({ label, value, required }) => (
  <div className="flex items-center justify-between text-sm py-1.5 border-b border-slate-100 last:border-0">
    <span className="text-slate-500">{label}</span>
    <span className={`font-semibold ${value ? 'text-slate-800' : (required ? 'text-rose-500' : 'text-slate-300')}`}>
      {value || (required ? '⚠ obrigatório' : '—')}
    </span>
  </div>
);

// Field memoizado fora do componente principal (impede unmount/remount → preserva foco do input)
export const Field = ({ label, children, error, full, hint, required }) => (
  <div className={full ? 'col-span-2' : ''}>
    <Label className="text-xs text-slate-600 flex items-center gap-1">
      {label}
      {required && <span className="text-rose-500">*</span>}
    </Label>
    <div className={error ? 'rounded-md ring-2 ring-rose-300/70' : ''}>
      {children}
    </div>
    {hint && !error && <p className="text-[10px] text-slate-400 mt-0.5">{hint}</p>}
    {error && <p className="text-[11px] text-rose-600 mt-0.5 flex items-center gap-1"><AlertCircle className="w-3 h-3" />{error}</p>}
  </div>
);

// Card de histórico anterior (preview + importar). Patch mínimo, sem dialogs extras.
export const HistoricoCard = ({ data, loading, dismissed, hasNotes, onApply, onDismiss, mode }) => {
  if (loading) {
    return (
      <div className="rounded-xl border border-violet-200 bg-violet-50/50 px-4 py-3 flex items-center gap-2 text-sm text-violet-700" data-testid="wiz-historico-loading">
        <Loader2 className="w-4 h-4 animate-spin" /> Buscando histórico anterior…
      </div>
    );
  }
  if (!data || !data.found || dismissed) return null;
  const medias = data.medias_por_disciplina || {};
  const discCount = Object.keys(medias).length;
  return (
    <div className="rounded-xl border border-emerald-200 bg-gradient-to-br from-emerald-50 to-white px-4 py-3" data-testid="wiz-historico-card">
      <div className="flex items-start gap-3">
        <div className="w-9 h-9 rounded-lg bg-emerald-100 flex items-center justify-center shrink-0">
          <History className="w-4 h-4 text-emerald-700" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-emerald-900 flex items-center gap-1.5">
            <Sparkles className="w-3.5 h-3.5 text-emerald-600" />
            Histórico de {data.year} encontrado
            {mode === 'new' && <span className="text-[10px] font-normal text-emerald-600 ml-1">· cadastro existente</span>}
          </p>
          <p className="text-xs text-slate-600 mt-0.5">
            <strong>{data.name}</strong> · {data.class_name || 'sem turma'} · {data.situation || '—'}
            {data.registration_number ? ` · matr. ${data.registration_number}` : ''}
          </p>
          <div className="grid grid-cols-3 gap-2 mt-2 text-[11px]">
            <div className="rounded-md bg-white border border-emerald-100 px-2 py-1">
              <span className="text-slate-500 block">Frequência</span>
              <span className="font-semibold text-slate-800">{data.frequencia_pct != null ? `${data.frequencia_pct}%` : '—'}</span>
            </div>
            <div className="rounded-md bg-white border border-emerald-100 px-2 py-1">
              <span className="text-slate-500 block">Média geral</span>
              <span className="font-semibold text-slate-800">{data.media_geral != null ? data.media_geral : '—'}</span>
            </div>
            <div className="rounded-md bg-white border border-emerald-100 px-2 py-1">
              <span className="text-slate-500 block">Disciplinas</span>
              <span className="font-semibold text-slate-800">{discCount}</span>
            </div>
          </div>
          {discCount > 0 && (
            <div className="mt-2 flex flex-wrap gap-1">
              {Object.entries(medias).slice(0, 8).map(([k, v]) => (
                <span key={k} className="text-[10px] bg-white border border-slate-200 rounded px-1.5 py-0.5 text-slate-700">
                  {k}: <strong>{v}</strong>
                </span>
              ))}
            </div>
          )}
          {data.ai_insight && (
            <div className="mt-2 flex items-start gap-1.5 rounded-md bg-gradient-to-r from-violet-50 via-fuchsia-50 to-violet-50 border border-violet-200 px-2 py-1.5" data-testid="wiz-historico-ai">
              <Sparkles className="w-3.5 h-3.5 text-violet-600 shrink-0 mt-0.5" />
              <p className="text-[11px] leading-snug text-violet-900 italic">{data.ai_insight}</p>
            </div>
          )}
        </div>
      </div>
      <div className="flex items-center justify-end gap-2 mt-3">
        <button onClick={onDismiss} className="text-xs text-slate-500 hover:text-slate-700 px-2 py-1" data-testid="wiz-historico-dismiss">
          Ignorar
        </button>
        <Button size="sm" onClick={onApply} className="bg-emerald-600 hover:bg-emerald-700 h-8 text-xs" data-testid="wiz-historico-apply">
          <History className="w-3.5 h-3.5 mr-1" />
          {hasNotes ? 'Anexar histórico' : 'Importar histórico'}
        </Button>
      </div>
    </div>
  );
};


// ═══════════════════════════════════════════════════════════════════════
//  FASE 2 — Etapa "Responsáveis"
//  • Múltiplos responsáveis (Financeiro principal + Pedagógico principal + Autorizados)
//  • Deduplicação automática via /api/responsibles-dedup/search (CPF → email → telefone)
//  • Cadastro inline via /api/responsibles-dedup/find-or-create
//  • Herança de endereço entre responsáveis
//  • Alerta visual quando responsável financeiro tem cadastro incompleto
// ═══════════════════════════════════════════════════════════════════════
export const ResponsaveisStep = ({ form, set, errors, responsibles, onResponsiblesUpdate }) => {
  const [showQuickNew, setShowQuickNew] = useState(false);
  const [quickRole, setQuickRole] = useState('financeiro'); // financeiro | pedagogico
  const [quickForm, setQuickForm] = useState({});
  const [quickSearch, setQuickSearch] = useState({ found: false, data: null, searching: false });
  const [saving, setSaving] = useState(false);
  const respFin = responsibles.find(r => r.id === form.responsible_id);
  const respPed = responsibles.find(r => r.id === form.responsible_didactic_id);
  const finIncomplete = respFin && isResponsavelIncomplete(respFin);
  const pickups = Array.isArray(form.authorized_pickups) ? form.authorized_pickups : [];

  // Migração silenciosa: se há autorizado legacy (form.authorized_pickup) e a lista está vazia, sobe
  useEffect(() => {
    if (pickups.length === 0 && (form.authorized_pickup || form.authorized_pickup_phone)) {
      set('authorized_pickups', [{
        name: form.authorized_pickup || '',
        phone: form.authorized_pickup_phone || '',
        relation: form.authorized_pickup_relation || '',
      }]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Debounced dedup lookup quando o usuário digita CPF/email/telefone no cadastro rápido
  useEffect(() => {
    if (!showQuickNew) { setQuickSearch({ found: false, data: null, searching: false }); return; }
    const cpfDigits = (quickForm.cpf || '').replace(/\D/g, '');
    const email = (quickForm.email || '').trim();
    const phoneDigits = (quickForm.cellphone || quickForm.phone || '').replace(/\D/g, '');
    if (cpfDigits.length < 11 && !email && phoneDigits.length < 8) {
      setQuickSearch({ found: false, data: null, searching: false });
      return;
    }
    setQuickSearch(s => ({ ...s, searching: true }));
    const id = setTimeout(async () => {
      try {
        const params = new URLSearchParams();
        if (cpfDigits.length === 11) params.set('cpf', cpfDigits);
        if (email && validEmail(email)) params.set('email', email);
        if (phoneDigits.length >= 8) params.set('phone', phoneDigits);
        if (!params.toString()) { setQuickSearch({ found: false, data: null, searching: false }); return; }
        const token = localStorage.getItem('token');
        const res = await fetch(`${API_URL}/api/responsibles-dedup/search?${params}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await res.json();
        setQuickSearch({ found: !!data.found, data: data.found ? data : null, searching: false });
      } catch {
        setQuickSearch({ found: false, data: null, searching: false });
      }
    }, 500);
    return () => clearTimeout(id);
  }, [showQuickNew, quickForm.cpf, quickForm.email, quickForm.cellphone, quickForm.phone]);

  const linkExisting = (id, role) => {
    if (role === 'financeiro') set('responsible_id', id);
    else if (role === 'pedagogico') set('responsible_didactic_id', id);
    setShowQuickNew(false);
    setQuickForm({});
    setQuickSearch({ found: false, data: null, searching: false });
    toast.success('Responsável vinculado');
  };

  const inheritAddressFromFinanceiro = () => {
    if (!respFin) { toast.error('Selecione antes o Responsável Financeiro'); return; }
    setQuickForm(q => ({
      ...q,
      zip_code: respFin.zip_code || q.zip_code,
      address: respFin.address || q.address,
      address_number: respFin.address_number || q.address_number,
      complement: respFin.complement || q.complement,
      neighborhood: respFin.neighborhood || q.neighborhood,
      city: respFin.city || q.city,
      state: respFin.state || q.state,
    }));
    toast.info('Endereço herdado do Responsável Financeiro');
  };

  // Helper: aplica endereço do ALUNO em qualquer destino (quickForm ou responsável existente)
  const studentAddress = () => ({
    zip_code: form.zip_code || '',
    address: form.address || '',
    address_number: form.address_number || '',
    complement: form.complement || '',
    neighborhood: form.neighborhood || '',
    city: form.city || '',
    state: form.state || '',
  });
  const studentHasFullAddress = REQUIRED_BANK_FIELDS.filter(k => k !== 'cpf').every(k => (form[k] || '').toString().trim());
  const inheritAddressFromAluno = () => {
    if (!studentHasFullAddress) {
      toast.error('Aluno ainda não tem endereço completo (CEP, logradouro, número, bairro, cidade, UF).');
      return;
    }
    const a = studentAddress();
    setQuickForm(q => ({
      ...q,
      zip_code: q.zip_code || a.zip_code,
      address: q.address || a.address,
      address_number: q.address_number || a.address_number,
      complement: q.complement || a.complement,
      neighborhood: q.neighborhood || a.neighborhood,
      city: q.city || a.city,
      state: q.state || a.state,
    }));
    toast.success('Endereço do aluno aplicado');
  };

  // Edição inline de responsável existente
  const [editResp, setEditResp] = useState(null); // {id, name, cpf, email, cellphone, phone, ...}
  const [editRespBusy, setEditRespBusy] = useState(false);
  const openEditResp = (resp) => {
    if (!resp) return;
    setEditResp({
      id: resp.id, name: resp.name || '', cpf: resp.cpf || '',
      rg: resp.rg || '', rg_orgao: resp.rg_orgao || '',
      profession: resp.profession || '', birth_date: resp.birth_date || '',
      email: resp.email || '', cellphone: resp.cellphone || '', phone: resp.phone || '',
      whatsapp: resp.whatsapp || '',
      zip_code: resp.zip_code || '', address: resp.address || '', address_number: resp.address_number || '',
      complement: resp.complement || '', neighborhood: resp.neighborhood || '',
      city: resp.city || '', state: resp.state || '',
      relation_type: resp.relation_type || 'Pai/Mãe',
      is_financial_responsible: resp.is_financial_responsible !== false,
    });
  };
  const editRespApplyStudentAddress = () => {
    if (!studentHasFullAddress) { toast.error('Aluno ainda não tem endereço completo.'); return; }
    const a = studentAddress();
    setEditResp(e => ({
      ...e,
      zip_code: e.zip_code || a.zip_code,
      address: e.address || a.address,
      address_number: e.address_number || a.address_number,
      complement: e.complement || a.complement,
      neighborhood: e.neighborhood || a.neighborhood,
      city: e.city || a.city,
      state: e.state || a.state,
    }));
    toast.success('Endereço do aluno aplicado');
  };
  const saveEditResp = async () => {
    if (!editResp) return;
    if (!editResp.name?.trim()) return toast.error('Nome obrigatório');
    const cpfDigits = (editResp.cpf || '').replace(/\D/g, '');
    if (cpfDigits.length !== 11) return toast.error('CPF inválido');
    setEditRespBusy(true);
    try {
      const token = localStorage.getItem('token');
      const payload = { ...editResp, cpf: cpfDigits };
      const res = await fetch(`${API_URL}/api/responsibles/${editResp.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(payload),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(formatApiError(data, 'Erro ao salvar responsável'));
      toast.success('Responsável atualizado');
      onResponsiblesUpdate?.();
      setEditResp(null);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setEditRespBusy(false);
    }
  };

  const submitQuick = async () => {
    if (!quickForm.name?.trim()) { toast.error('Nome é obrigatório'); return; }
    const cpfDigits = (quickForm.cpf || '').replace(/\D/g, '');
    if (cpfDigits.length !== 11) { toast.error('CPF válido é obrigatório'); return; }
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/responsibles-dedup/find-or-create`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(quickForm),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Erro ao salvar');
      if (data.is_new) toast.success(`Responsável "${data.name}" cadastrado`);
      else toast.info(`Responsável existente vinculado (${data.match_via})`);
      // Atualiza lista de responsáveis no parent (e seleciona)
      onResponsiblesUpdate?.();
      // Vincula ao papel escolhido (timeout para o reload chegar antes da seleção)
      setTimeout(() => linkExisting(data.id, quickRole), 50);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSaving(false);
    }
  };

  const addPickup = () => set('authorized_pickups', [...pickups, { name: '', phone: '', relation: '' }]);
  const updatePickup = (i, k, v) => {
    const next = [...pickups];
    next[i] = { ...next[i], [k]: v };
    set('authorized_pickups', next);
  };
  const removePickup = (i) => set('authorized_pickups', pickups.filter((_, idx) => idx !== i));

  return (
    <div className="space-y-4">
      {/* ─── V9.7 · vínculos reais do aluno (guardians canônicos) ─── */}
      {form.id && (
        <div className="rounded-xl border border-slate-200 bg-slate-50/60 p-3.5">
          <GuardiansLinkedList studentId={form.id} studentName={form.name} student={form} scope="-cadastro"
            onEditGuardian={(g) => setEditResp({ ...g })} />
        </div>
      )}

      {/* V9.11 · aluno já salvo usa SÓ o card único "Responsáveis vinculados"; a UI antiga fica no cadastro inicial */}
      <div className={form.id ? 'hidden' : 'space-y-4'} data-testid="wiz-resp-legacy">
      {/* ─── HEADER: título + ações ─── */}
      <div className="flex items-start justify-between gap-3 mb-1">
        <div>
          <p className="text-base font-bold text-slate-900">Responsáveis pelo Aluno</p>
          <p className="text-xs text-slate-500">Cadastre as pessoas responsáveis pelo aluno. O responsável financeiro será o titular dos boletos.</p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {studentHasFullAddress && (
            <button
              type="button"
              onClick={() => {
                const ids = [...new Set([form.responsible_id, form.responsible_didactic_id, form.responsible_financial_optional_id].filter(Boolean))];
                if (!ids.length) { toast.error('Vincule pelo menos um responsável'); return; }
                set('apply_aluno_address_to_responsibles', true);
                toast.success('Será aplicado ao salvar (responsáveis sem endereço completo)');
              }}
              className="inline-flex items-center gap-1.5 rounded-xl border-2 border-violet-200 bg-violet-50/60 hover:bg-violet-100/60 text-violet-700 font-semibold text-xs px-3.5 py-2 transition-colors"
              data-testid="wiz-resp-apply-bulk-cta"
            >
              <Copy className="w-3.5 h-3.5" />
              Aplicar endereço do aluno aos responsáveis
            </button>
          )}
          <button
            type="button"
            onClick={() => { setQuickRole('financeiro'); setShowQuickNew(true); setQuickForm({}); }}
            className="inline-flex items-center gap-1.5 rounded-xl bg-gradient-to-r from-violet-600 to-violet-700 hover:from-violet-700 hover:to-violet-800 text-white font-semibold text-xs px-3.5 py-2 shadow-md shadow-violet-500/20"
            data-testid="wiz-resp-novo"
          >
            <Plus className="w-3.5 h-3.5" />
            Novo Responsável
          </button>
        </div>
      </div>

      {/* ─── 2 COLUNAS (sidebar info + cards) ─── */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* SIDEBAR (1/3) */}
        <div className="space-y-3 lg:col-span-1">
          {/* Endereço do Aluno */}
          <div className="rounded-2xl border border-slate-200 bg-white p-4">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm font-bold text-slate-800">Endereço do Aluno</p>
              <span className={`inline-flex items-center text-[10px] font-bold px-2 py-0.5 rounded-full ${studentHasFullAddress ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-amber-50 text-amber-700 border border-amber-200'}`} data-testid="wiz-aluno-addr-status">
                {studentHasFullAddress ? 'Completo' : 'Incompleto'}
              </span>
            </div>
            <div className="flex items-start gap-2.5 py-2">
              <div className="w-8 h-8 rounded-xl bg-emerald-50 text-emerald-600 flex items-center justify-center shrink-0">
                <Home className="w-4 h-4" />
              </div>
              <div className="text-[12.5px] text-slate-600 leading-snug min-w-0">
                {studentHasFullAddress ? (
                  <>
                    <p className="truncate">{form.address}, {form.address_number}</p>
                    {form.complement && <p className="truncate">{form.complement}</p>}
                    <p className="truncate">{form.neighborhood}</p>
                    <p>{form.city} - {form.state}</p>
                    <p className="text-[11px] text-slate-400">CEP: {form.zip_code}</p>
                  </>
                ) : (
                  <p className="text-amber-700">Preencha em <em>Contato &amp; Endereço</em>.</p>
                )}
              </div>
            </div>
            <button type="button" onClick={() => toast.info('Edite no bloco "Contato & Endereço" desta mesma tela.')}
              className="w-full mt-1 text-xs font-semibold text-emerald-700 hover:text-emerald-900 border border-emerald-200 hover:bg-emerald-50 rounded-xl py-2 transition-colors"
              data-testid="wiz-aluno-addr-edit-link"
            >
              Ver / Editar endereço do aluno
            </button>
          </div>

          {/* Ações em massa */}
          <div className="rounded-2xl border border-slate-200 bg-white p-4">
            <p className="text-sm font-bold text-slate-800 mb-1">Ações em massa</p>
            <p className="text-[11.5px] text-slate-500 leading-snug">
              Aplique o endereço do aluno para múltiplos responsáveis ou alunos de uma vez.
            </p>
            <button
              type="button"
              disabled={!studentHasFullAddress}
              onClick={() => {
                set('apply_aluno_address_to_responsibles', true);
                toast.success('Os responsáveis sem endereço receberão o endereço do aluno ao salvar.');
              }}
              className="w-full mt-3 inline-flex items-center justify-center gap-1.5 rounded-xl border-2 border-emerald-200 hover:bg-emerald-50 text-emerald-700 font-semibold text-xs py-2.5 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="wiz-resp-bulk-toggle"
            >
              <Users className="w-4 h-4" />
              Aplicar em massa
            </button>
          </div>
        </div>

        {/* CARDS DE RESPONSÁVEIS (2/3) */}
        <div className="space-y-3 lg:col-span-2">
          {(() => {
            const slots = [
              { id: form.responsible_id, label: 'Responsável Financeiro', tag: 'Titular', tagColor: 'bg-violet-50 text-violet-700 border-violet-200', icon: Users, edge: 'border-l-4 border-l-violet-500', set_field: 'responsible_id' },
              { id: form.responsible_didactic_id, label: 'Responsável Pedagógico', tag: 'Responsável Legal', tagColor: 'bg-emerald-50 text-emerald-700 border-emerald-200', icon: GraduationCap, edge: 'border-l-4 border-l-emerald-500', set_field: 'responsible_didactic_id' },
            ];
            const cards = slots.filter(s => s.id);
            return (
              <>
                {/* SELECTS originais quando ainda não há vínculos (compatibilidade total) */}
                {!form.responsible_id && (
                  <div className="rounded-2xl border-2 border-dashed border-violet-200 bg-violet-50/30 p-4">
                    <p className="text-xs uppercase tracking-wider font-semibold text-violet-700 mb-2 flex items-center gap-1.5">
                      <Users className="w-3.5 h-3.5" /> Vincular Responsável Financeiro *
                    </p>
                    <Select value={form.responsible_id || ''} onValueChange={v => set('responsible_id', v)}>
                      <SelectTrigger data-testid="wiz-responsible"><SelectValue placeholder="Selecione um responsável" /></SelectTrigger>
                      <SelectContent className="max-h-72">
                        {responsibles.map(r => (
                          <SelectItem key={r.id} value={r.id}>
                            {r.name}{r.cpf ? ` · ${r.cpf}` : ''}{isResponsavelIncomplete(r) ? ' ⚠' : ''}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {errors.responsible_id && <p className="text-[11px] text-rose-600 flex items-center gap-1 mt-1.5"><AlertCircle className="w-3 h-3" />{errors.responsible_id}</p>}
                  </div>
                )}

                {/* Cards visuais para responsáveis já vinculados */}
                {cards.map((slot) => {
                  const r = responsibles.find(x => x.id === slot.id);
                  if (!r) return null;
                  const incomplete = isResponsavelIncomplete(r);
                  const Icon = slot.icon;
                  return (
                    <div key={slot.set_field} className={`rounded-2xl bg-white border border-slate-200 ${slot.edge} shadow-sm hover:shadow-md transition-shadow`} data-testid={`wiz-resp-card-${slot.set_field}`}>
                      <div className="px-4 pt-3 pb-2 flex items-center gap-2 border-b border-slate-100">
                        <Icon className={`w-3.5 h-3.5 ${slot.set_field === 'responsible_id' ? 'text-violet-600' : 'text-emerald-600'}`} />
                        <p className={`text-[11px] uppercase tracking-wider font-bold ${slot.set_field === 'responsible_id' ? 'text-violet-700' : 'text-emerald-700'}`}>{slot.label}</p>
                      </div>
                      <div className="p-4">
                        <div className="flex items-start gap-3">
                          <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-slate-100 to-slate-200 flex items-center justify-center shrink-0 ring-2 ring-white shadow-sm">
                            <UserIcon className="w-6 h-6 text-slate-400" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <p className="text-sm font-bold text-slate-800 truncate">{r.name}</p>
                              <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full border ${slot.tagColor}`}>{slot.tag}</span>
                              <span className="inline-flex items-center text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">Ativo</span>
                            </div>
                            <p className="text-[11px] text-slate-500 mt-0.5">
                              {r.relation_type || '—'}{r.cpf ? ` · ${r.cpf}` : ''}{r.profession ? ` · ${r.profession}` : ''}{r.birth_date ? ` · nasc. ${String(r.birth_date).slice(0,10).split('-').reverse().join('/')}` : ''}
                            </p>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-3 gap-y-0.5 mt-2 text-[12px]">
                              <p className="text-slate-600 truncate flex items-center gap-1.5"><Phone className="w-3 h-3 text-slate-400" />{r.cellphone || r.phone || '—'}</p>
                              <p className="text-slate-600 truncate flex items-center gap-1.5"><Mail className="w-3 h-3 text-slate-400" />{r.email || '—'}</p>
                            </div>
                          </div>
                          <div className="flex flex-col items-end gap-2 shrink-0">
                            <span className={`inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full border ${incomplete ? 'bg-amber-50 text-amber-700 border-amber-200' : 'bg-emerald-50 text-emerald-700 border-emerald-200'}`}>
                              {incomplete ? <AlertCircle className="w-2.5 h-2.5" /> : <CheckCircle2 className="w-2.5 h-2.5" />}
                              Endereço {incomplete ? 'Incompleto' : 'Completo'}
                            </span>
                            <button type="button" onClick={() => openEditResp(r)} className="inline-flex items-center gap-1 rounded-lg border border-slate-200 hover:bg-slate-50 text-slate-700 text-[11px] font-semibold px-2.5 py-1" data-testid={`wiz-resp-card-edit-${slot.set_field}`}>
                              <Edit2 className="w-3 h-3" /> Editar
                            </button>
                          </div>
                        </div>

                        {/* Linha de RG / Endereço */}
                        <div className="mt-3 pt-3 border-t border-slate-100 grid grid-cols-2 lg:grid-cols-4 gap-3 text-[11px]">
                          <div>
                            <p className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">RG</p>
                            <p className={r.rg ? 'text-slate-700' : 'text-amber-600'}>{r.rg || 'Não informado'}</p>
                          </div>
                          <div>
                            <p className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">Órgão emissor</p>
                            <p className={r.rg_orgao ? 'text-slate-700' : 'text-amber-600'}>{r.rg_orgao || 'Não informado'}</p>
                          </div>
                          <div>
                            <p className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">Data de emissão</p>
                            <p className={r.rg_emissao || r.rg_issue_date ? 'text-slate-700' : 'text-amber-600'}>{r.rg_emissao || r.rg_issue_date || 'Não informado'}</p>
                          </div>
                          <div className="col-span-2 lg:col-span-1">
                            <p className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">Endereço</p>
                            {!incomplete ? (
                              <p className="text-slate-700 leading-tight">
                                {r.address}, {r.address_number}{r.complement ? `, ${r.complement}` : ''}<br />
                                {r.neighborhood}, {r.city} - {r.state}, CEP: {r.zip_code}
                              </p>
                            ) : (
                              <button
                                type="button"
                                disabled={!studentHasFullAddress}
                                onClick={async () => {
                                  if (!studentHasFullAddress) { toast.error('Aluno ainda não tem endereço completo'); return; }
                                  try {
                                    const a = studentAddress();
                                    const token = localStorage.getItem('token');
                                    const payload = { ...r, ...a };
                                    const res = await fetch(`${API_URL}/api/responsibles/${r.id}`, {
                                      method: 'PUT',
                                      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                                      body: JSON.stringify(payload),
                                    });
                                    const data = await res.json().catch(() => ({}));
                                    if (!res.ok) throw new Error(data.detail || 'Erro');
                                    toast.success('Endereço do aluno aplicado');
                                    onResponsiblesUpdate?.();
                                  } catch (e) { toast.error(e.message); }
                                }}
                                className="inline-flex items-center gap-1.5 rounded-lg border-2 border-amber-300 bg-amber-50/40 hover:bg-amber-50 text-amber-800 font-semibold text-[11px] px-2.5 py-1.5 disabled:opacity-50"
                                data-testid={`wiz-resp-card-use-aluno-${slot.set_field}`}
                              >
                                <Copy className="w-3 h-3" />
                                Usar endereço do aluno
                              </button>
                            )}
                          </div>
                        </div>

                        {finIncomplete && slot.set_field === 'responsible_id' && (
                          <div className="mt-3 flex items-start gap-2 rounded-lg border border-amber-300 bg-amber-50 px-2.5 py-2" data-testid="wiz-resp-fin-incomplete">
                            <AlertTriangle className="w-4 h-4 text-amber-700 shrink-0 mt-0.5" />
                            <div className="text-[11.5px] text-amber-900 leading-snug">
                              <strong>Boleto Safra bloqueado.</strong> Faltam: {missingBankFields(respFin).map(k => FIELD_LABELS_PT[k] || k).join(', ')}.
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}

                {/* Pedagógico — atalho compacto se ainda não vinculado */}
                {!form.responsible_didactic_id && form.responsible_id && (
                  <div className="rounded-2xl border-2 border-dashed border-emerald-200 bg-emerald-50/30 p-4">
                    <p className="text-xs uppercase tracking-wider font-semibold text-emerald-700 mb-2 flex items-center gap-1.5">
                      <GraduationCap className="w-3.5 h-3.5" /> Vincular Responsável Pedagógico
                    </p>
                    <Select value="" onValueChange={v => set('responsible_didactic_id', v === '__same__' ? form.responsible_id : v)}>
                      <SelectTrigger><SelectValue placeholder="Mesmo do financeiro ou selecionar outro" /></SelectTrigger>
                      <SelectContent className="max-h-72">
                        <SelectItem value="__same__">Mesmo do financeiro</SelectItem>
                        {responsibles.map(r => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </>
            );
          })()}
        </div>
      </div>

      {/* AUTORIZADOS A RETIRAR */}
      <div className="rounded-xl border border-blue-200 bg-blue-50/40 p-3 space-y-2" data-testid="wiz-pickup-block">
        <div className="flex items-center justify-between">
          <p className="text-xs uppercase tracking-wider font-semibold text-blue-700 flex items-center gap-1.5">
            <Users className="w-3.5 h-3.5" /> Autorizados a Retirar
          </p>
          <button
            type="button"
            onClick={addPickup}
            className="text-[11px] text-blue-700 hover:text-blue-900 font-semibold flex items-center gap-1"
            data-testid="wiz-pickup-add"
          >
            <Plus className="w-3 h-3" /> Adicionar
          </button>
        </div>
        {pickups.length === 0 && (
          <p className="text-[11px] text-slate-500 italic">Nenhum autorizado adicionado.</p>
        )}
        {pickups.map((p, i) => (
          <div key={i} className="grid grid-cols-12 gap-2 items-center bg-white rounded-md border border-blue-100 px-2 py-1.5" data-testid={`wiz-pickup-row-${i}`}>
            <Input className="col-span-5 h-8 text-xs" placeholder="Nome" value={p.name || ''} onChange={e => updatePickup(i, 'name', e.target.value)} />
            <Input className="col-span-3 h-8 text-xs" placeholder="Telefone" value={p.phone || ''} onChange={e => updatePickup(i, 'phone', maskPhone(e.target.value))} />
            <Input className="col-span-3 h-8 text-xs" placeholder="Parentesco" value={p.relation || ''} onChange={e => updatePickup(i, 'relation', e.target.value)} />
            <button type="button" onClick={() => removePickup(i)} className="col-span-1 p-1 rounded hover:bg-rose-50 justify-self-center" data-testid={`wiz-pickup-remove-${i}`}>
              <Trash2 className="w-3.5 h-3.5 text-rose-500" />
            </button>
          </div>
        ))}
      </div>

      {/* Cadastro Rápido (FASE 2) — modal inline */}
      {showQuickNew && (
        <div className="rounded-xl border-2 border-violet-300 bg-white p-3 space-y-2 shadow-sm" data-testid="wiz-quick-new">
          <div className="flex items-center justify-between">
            <p className="text-xs uppercase tracking-wider font-semibold text-violet-800 flex items-center gap-1.5">
              <Plus className="w-3.5 h-3.5" /> Novo {quickRole === 'financeiro' ? 'Responsável Financeiro' : 'Responsável Pedagógico'}
            </p>
            <button type="button" onClick={() => setShowQuickNew(false)} className="p-1 hover:bg-slate-100 rounded">
              <X className="w-3.5 h-3.5 text-slate-500" />
            </button>
          </div>
          <p className="text-[10.5px] text-slate-500 leading-snug">
            <SearchIcon className="w-3 h-3 inline mr-0.5" /> Digite CPF, e-mail ou telefone — buscamos automaticamente para evitar duplicatas.
          </p>

          {/* Banner de match encontrado */}
          {quickSearch.searching && (
            <div className="flex items-center gap-2 text-[11px] text-violet-700"><Loader2 className="w-3 h-3 animate-spin" /> Buscando…</div>
          )}
          {quickSearch.found && quickSearch.data && (
            <div className="rounded-md border border-emerald-300 bg-emerald-50 px-2.5 py-2 flex items-start gap-2" data-testid="wiz-quick-match">
              <CheckCircle2 className="w-4 h-4 text-emerald-700 shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-[12px] font-semibold text-emerald-900">
                  Já existe: {quickSearch.data.name} <span className="text-[10px] font-normal">({quickSearch.data.match_via})</span>
                </p>
                <p className="text-[10.5px] text-emerald-800">
                  {quickSearch.data.cpf || ''} {quickSearch.data.email ? `· ${quickSearch.data.email}` : ''}
                </p>
              </div>
              <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 h-7 text-[11px]" onClick={() => linkExisting(quickSearch.data.id, quickRole)} data-testid="wiz-quick-link">
                <Link2 className="w-3 h-3 mr-1" /> Vincular
              </Button>
            </div>
          )}

          <div className="grid grid-cols-2 gap-2">
            <Field label="Nome completo *" full>
              <Input value={quickForm.name || ''} onChange={e => setQuickForm(q => ({ ...q, name: e.target.value }))} data-testid="wiz-quick-name" />
            </Field>
            <Field label="CPF *">
              <Input value={quickForm.cpf || ''} onChange={e => setQuickForm(q => ({ ...q, cpf: maskCPF(e.target.value) }))} placeholder="000.000.000-00" data-testid="wiz-quick-cpf" />
            </Field>
            <Field label="RG"><Input value={quickForm.rg || ''} onChange={e => setQuickForm(q => ({ ...q, rg: e.target.value }))} data-testid="wiz-quick-rg" /></Field>
            <Field label="Órgão emissor">
              <Input value={quickForm.rg_orgao || ''} onChange={e => setQuickForm(q => ({ ...q, rg_orgao: e.target.value.toUpperCase() }))} placeholder="SSP/SP" maxLength={10} data-testid="wiz-quick-rg-orgao" />
            </Field>
            <Field label="E-mail" full>
              <Input type="email" value={quickForm.email || ''} onChange={e => setQuickForm(q => ({ ...q, email: e.target.value }))} data-testid="wiz-quick-email" />
            </Field>
            <Field label="Celular / WhatsApp">
              <Input value={quickForm.cellphone || ''} onChange={e => setQuickForm(q => ({ ...q, cellphone: maskPhone(e.target.value) }))} data-testid="wiz-quick-cellphone" />
            </Field>
            <Field label="Telefone fixo">
              <Input value={quickForm.phone || ''} onChange={e => setQuickForm(q => ({ ...q, phone: maskPhone(e.target.value) }))} />
            </Field>
            <Field label="Profissão">
              <Input value={quickForm.profession || ''} onChange={e => setQuickForm(q => ({ ...q, profession: e.target.value }))} placeholder="Ex.: Enfermeira" data-testid="wiz-quick-profession" />
            </Field>
            <Field label="Data de nascimento">
              <Input type="date" value={quickForm.birth_date || ''} onChange={e => setQuickForm(q => ({ ...q, birth_date: e.target.value }))} data-testid="wiz-quick-birth-date" />
            </Field>
            <Field label="Parentesco">
              <Select value={quickForm.relation_type || 'Pai/Mãe'} onValueChange={v => setQuickForm(q => ({ ...q, relation_type: v }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {['Pai/Mãe','Pai','Mãe','Avô/Avó','Tio/Tia','Padrasto/Madrasta','Tutor Legal','Outro'].map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
          </div>

          {/* Endereço — com herança opcional */}
          <div className="border-t border-slate-100 pt-2 mt-1">
            <div className="flex items-center justify-between mb-1">
              <p className="text-[11px] font-semibold text-slate-600">Endereço (necessário para boleto Safra)</p>
              <div className="flex items-center gap-3">
                {studentHasFullAddress && (
                  <button type="button" onClick={inheritAddressFromAluno} className="text-[10.5px] text-emerald-700 hover:text-emerald-900 flex items-center gap-1" data-testid="wiz-quick-inherit-from-aluno">
                    <Copy className="w-3 h-3" /> Usar endereço do aluno
                  </button>
                )}
                {respFin && quickRole !== 'financeiro' && (
                  <button type="button" onClick={inheritAddressFromFinanceiro} className="text-[10.5px] text-violet-700 hover:text-violet-900 flex items-center gap-1" data-testid="wiz-quick-inherit-address">
                    <Copy className="w-3 h-3" /> Herdar do financeiro
                  </button>
                )}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <Field label="CEP">
                <Input
                  value={quickForm.zip_code || ''}
                  onChange={e => setQuickForm(q => ({ ...q, zip_code: maskCEP(e.target.value) }))}
                  onBlur={async (e) => {
                    const data = await fetchCEP(e.target.value);
                    if (!data) return;
                    setQuickForm(q => ({
                      ...q,
                      address: q.address || data.address,
                      neighborhood: q.neighborhood || data.neighborhood,
                      city: q.city || data.city,
                      state: q.state || data.state,
                    }));
                  }}
                  data-testid="wiz-quick-cep"
                />
              </Field>
              <Field label="Endereço"><Input value={quickForm.address || ''} onChange={e => setQuickForm(q => ({ ...q, address: e.target.value }))} /></Field>
              <Field label="Número"><Input value={quickForm.address_number || ''} onChange={e => setQuickForm(q => ({ ...q, address_number: e.target.value }))} /></Field>
              <Field label="Complemento"><Input value={quickForm.complement || ''} onChange={e => setQuickForm(q => ({ ...q, complement: e.target.value }))} /></Field>
              <Field label="Bairro"><Input value={quickForm.neighborhood || ''} onChange={e => setQuickForm(q => ({ ...q, neighborhood: e.target.value }))} /></Field>
              <Field label="Cidade"><Input value={quickForm.city || ''} onChange={e => setQuickForm(q => ({ ...q, city: e.target.value }))} /></Field>
              <Field label="UF"><Input maxLength={2} value={quickForm.state || ''} onChange={e => setQuickForm(q => ({ ...q, state: e.target.value.toUpperCase() }))} /></Field>
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-1">
            <Button variant="ghost" size="sm" onClick={() => setShowQuickNew(false)}>Cancelar</Button>
            <Button size="sm" onClick={submitQuick} disabled={saving} className="bg-violet-600 hover:bg-violet-700" data-testid="wiz-quick-save">
              {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Salvar e vincular</>}
            </Button>
          </div>
        </div>
      )}

      <p className="text-[10.5px] text-slate-400 pt-1 leading-snug">
        💡 Esses responsáveis alimentam Financeiro, DNA Pay, Boleto Safra, NFS-e e DNA Connect. Endereço completo é obrigatório para emissão de boleto bancário.
      </p>

      {/* ─── Modal: Editar responsável existente ─── */}
      <Dialog open={!!editResp} onOpenChange={(o) => !o && !editRespBusy && setEditResp(null)}>
        <DialogContent className="max-w-xl" data-testid="wiz-resp-edit-dialog">
          <VisuallyHidden.Root><DialogTitle>Editar responsável</DialogTitle></VisuallyHidden.Root>
          {editResp && (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <p className="text-sm font-bold text-slate-800">Editar responsável</p>
                {studentHasFullAddress && (
                  <button type="button" onClick={editRespApplyStudentAddress} className="text-[11px] text-emerald-700 hover:text-emerald-900 font-semibold flex items-center gap-1" data-testid="wiz-resp-edit-apply-aluno">
                    <Copy className="w-3 h-3" /> Usar endereço do aluno
                  </button>
                )}
              </div>
              <div className="grid grid-cols-2 gap-2">
                <Field label="Nome *" full>
                  <Input value={editResp.name} onChange={e => setEditResp(s => ({ ...s, name: e.target.value }))} data-testid="wiz-resp-edit-name" />
                </Field>
                <Field label="CPF *">
                  <Input value={editResp.cpf} onChange={e => setEditResp(s => ({ ...s, cpf: maskCPF(e.target.value) }))} placeholder="000.000.000-00" />
                </Field>
                <Field label="RG">
                  <Input value={editResp.rg || ''} onChange={e => setEditResp(s => ({ ...s, rg: e.target.value }))} data-testid="wiz-resp-edit-rg" />
                </Field>
                <Field label="Órgão emissor">
                  <Input value={editResp.rg_orgao || ''} onChange={e => setEditResp(s => ({ ...s, rg_orgao: e.target.value.toUpperCase() }))} placeholder="SSP/SP" maxLength={10} data-testid="wiz-resp-edit-rg-orgao" />
                </Field>
                <Field label="E-mail">
                  <Input type="email" value={editResp.email} onChange={e => setEditResp(s => ({ ...s, email: e.target.value }))} />
                </Field>
                <Field label="Celular / WhatsApp">
                  <Input value={editResp.cellphone} onChange={e => setEditResp(s => ({ ...s, cellphone: maskPhone(e.target.value) }))} />
                </Field>
                <Field label="Telefone fixo">
                  <Input value={editResp.phone} onChange={e => setEditResp(s => ({ ...s, phone: maskPhone(e.target.value) }))} />
                </Field>
                <Field label="Profissão">
                  <Input value={editResp.profession || ''} onChange={e => setEditResp(s => ({ ...s, profession: e.target.value }))} placeholder="Ex.: Enfermeira" data-testid="wiz-resp-edit-profession" />
                </Field>
                <Field label="Data de nascimento">
                  <Input type="date" value={editResp.birth_date || ''} onChange={e => setEditResp(s => ({ ...s, birth_date: e.target.value }))} data-testid="wiz-resp-edit-birth-date" />
                </Field>
              </div>
              <div className="border-t border-slate-100 pt-2">
                <p className="text-[11px] font-semibold text-slate-600 mb-1">Endereço (obrigatório para boleto Safra)</p>
                <div className="grid grid-cols-2 gap-2">
                  <Field label="CEP">
                    <Input
                      value={editResp.zip_code}
                      onChange={e => setEditResp(s => ({ ...s, zip_code: maskCEP(e.target.value) }))}
                      onBlur={async (e) => {
                        const data = await fetchCEP(e.target.value);
                        if (!data) return;
                        setEditResp(s => ({
                          ...s,
                          address: s.address || data.address,
                          neighborhood: s.neighborhood || data.neighborhood,
                          city: s.city || data.city,
                          state: s.state || data.state,
                        }));
                      }}
                      data-testid="wiz-resp-edit-cep"
                    />
                  </Field>
                  <Field label="Endereço"><Input value={editResp.address} onChange={e => setEditResp(s => ({ ...s, address: e.target.value }))} /></Field>
                  <Field label="Número"><Input value={editResp.address_number} onChange={e => setEditResp(s => ({ ...s, address_number: e.target.value }))} /></Field>
                  <Field label="Complemento"><Input value={editResp.complement} onChange={e => setEditResp(s => ({ ...s, complement: e.target.value }))} /></Field>
                  <Field label="Bairro"><Input value={editResp.neighborhood} onChange={e => setEditResp(s => ({ ...s, neighborhood: e.target.value }))} /></Field>
                  <Field label="Cidade"><Input value={editResp.city} onChange={e => setEditResp(s => ({ ...s, city: e.target.value }))} /></Field>
                  <Field label="UF"><Input maxLength={2} value={editResp.state} onChange={e => setEditResp(s => ({ ...s, state: e.target.value.toUpperCase() }))} /></Field>
                </div>
              </div>
              <div className="flex justify-end gap-2 pt-1">
                <Button variant="ghost" size="sm" onClick={() => setEditResp(null)} disabled={editRespBusy}>Cancelar</Button>
                <Button size="sm" onClick={saveEditResp} disabled={editRespBusy} className="bg-violet-600 hover:bg-violet-700" data-testid="wiz-resp-edit-save">
                  {editRespBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Salvar</>}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════
//  FASE 3 — Etapa "Financeiro"
//   • Lista planos do curso da turma escolhida (course_financial_plans)
//   • Permite selecionar múltiplos via checkbox
//   • Persiste em form.financial_plans (POST/PUT students gera títulos automaticamente)
//   • Modo edit: mostra avisos de "novos planos serão aplicados ao salvar"
// ═══════════════════════════════════════════════════════════════════════
const PLAN_CATEGORY_LABELS = {
  mensalidade: 'Mensalidade',
  material: 'Material',
  matricula: 'Matrícula',
  bilingue: 'Bilíngue',
  integral_almoco: 'Integral c/ Almoço',
  integral_sem_almoco: 'Integral s/ Almoço',
};
const fmtBRL = (v) => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(Number(v) || 0);

export const FinanceiroStep = ({ form, set, classGroups, mode, studentId }) => {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [editingPlan, setEditingPlan] = useState(null); // {id, name, num_installments, installment_value, abatement, first_installment_different_value, first_installment_value}
  const [editBusy, setEditBusy] = useState(false);
  const [deletePlanConfirm, setDeletePlanConfirm] = useState(null); // { plan }
  const [deleteBusy, setDeleteBusy] = useState(false);
  const cg = classGroups.find(c => c.id === form.class_group_id);
  const courseId = cg?.course_id || null;
  const selectedIds = Array.isArray(form.financial_plans) ? form.financial_plans : [];
  const initialPlansRef = useRef(null);
  if (initialPlansRef.current === null && mode === 'edit') {
    initialPlansRef.current = new Set(selectedIds);
  }

  useEffect(() => {
    let alive = true;
    const fetchPlans = async () => {
      if (!courseId) { setPlans([]); return; }
      setLoading(true); setError(null);
      try {
        const token = localStorage.getItem('token');
        const res = await fetch(`${API_URL}/api/course-financial-plans?course_id=${courseId}&active=true`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await res.json();
        if (alive) setPlans(Array.isArray(data) ? data : []);
      } catch (e) {
        if (alive) setError('Erro ao carregar planos');
      } finally {
        if (alive) setLoading(false);
      }
    };
    fetchPlans();
    return () => { alive = false; };
  }, [courseId]);

  const togglePlan = (planId) => {
    const cur = new Set(selectedIds);
    if (cur.has(planId)) cur.delete(planId); else cur.add(planId);
    set('financial_plans', Array.from(cur));
  };

  const reloadPlans = async () => {
    if (!courseId) return;
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/course-financial-plans?course_id=${courseId}&active=true`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) setPlans(await res.json());
    } catch { /* noop */ }
  };

  const savePlanEdit = async () => {
    if (!editingPlan) return;
    const orig = editingPlan._full || {};
    const payload = {
      // PUT exige body completo (CourseFinancialPlanCreate). Preservamos campos não editados.
      course_id: editingPlan.course_id || orig.course_id,
      name: editingPlan.name,
      category: editingPlan.category || orig.category,
      description: orig.description || null,
      num_installments: Number(editingPlan.num_installments) || 1,
      installment_value: Number(editingPlan.installment_value) || 0,
      start_date: orig.start_date || null,
      due_day: Number(editingPlan.due_day) || 10,
      scholarship_id: orig.scholarship_id || null,
      abatement: Number(editingPlan.abatement) || 0,
      discount_enabled: !!orig.discount_enabled,
      discount_percent: Number(orig.discount_percent) || 50.0,
      discount_days_before: Number(orig.discount_days_before) || 3,
      first_installment_different_value: !!editingPlan.first_installment_different_value,
      first_installment_value: editingPlan.first_installment_different_value
        ? (Number(editingPlan.first_installment_value) || 0) : null,
      first_installment_different_date: !!orig.first_installment_different_date,
      first_installment_date: orig.first_installment_date || null,
      use_enrollment_date: !!orig.use_enrollment_date,
      use_current_month: !!orig.use_current_month,
      is_default: !!editingPlan.is_default,
      active: editingPlan.active !== false,
    };
    setEditBusy(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/course-financial-plans/${editingPlan.id}`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(typeof err.detail === 'string' ? err.detail : 'Erro ao atualizar plano');
      }
      toast.success('Plano atualizado');
      setEditingPlan(null);
      await reloadPlans();
    } catch (e) {
      toast.error(e.message || 'Erro ao atualizar plano');
    } finally {
      setEditBusy(false);
    }
  };

  const deletePlan = async () => {
    if (!deletePlanConfirm) return;
    const planId = deletePlanConfirm.plan.id;
    setDeleteBusy(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/course-financial-plans/${planId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(typeof err.detail === 'string' ? err.detail : 'Erro ao excluir plano');
      }
      // remove da seleção do aluno se estava marcado
      if (selectedIds.includes(planId)) {
        set('financial_plans', selectedIds.filter(id => id !== planId));
      }
      toast.success('Plano excluído');
      setDeletePlanConfirm(null);
      await reloadPlans();
    } catch (e) {
      toast.error(e.message || 'Erro ao excluir plano');
    } finally {
      setDeleteBusy(false);
    }
  };

  const totalEstimated = plans
    .filter(p => selectedIds.includes(p.id))
    .reduce((sum, p) => {
      const inst = Number(p.installment_value) || 0;
      const n = Number(p.num_installments) || 0;
      const firstDiff = p.first_installment_different_value && p.first_installment_value != null
        ? Number(p.first_installment_value) : inst;
      // Total estimado: 1ª parcela diferenciada + (n-1) * valor normal − abatimento
      const abat = (Number(p.abatement) || 0) * n;
      return sum + (firstDiff + Math.max(0, n - 1) * inst - abat);
    }, 0);

  const newSelections = mode === 'edit' && initialPlansRef.current
    ? selectedIds.filter(id => !initialPlansRef.current.has(id))
    : selectedIds;

  if (!courseId) {
    return (
      <div className="space-y-3" data-testid="wiz-fin-step">
        <div className="rounded-xl border border-dashed border-amber-300 bg-amber-50/40 p-5 text-center" data-testid="wiz-fin-no-class">
          <Wallet className="w-7 h-7 text-amber-500 mx-auto mb-2" />
          <p className="text-sm font-semibold text-amber-900">Selecione uma turma</p>
          <p className="text-xs text-amber-800 mt-1">Para listar os planos financeiros, escolha a turma na etapa "Dados do Aluno".</p>
        </div>
        {mode === 'edit' && studentId && (
          <FinanceiroTitulosExistentes studentId={studentId} />
        )}
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="wiz-fin-step">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-xs uppercase tracking-wider font-semibold text-violet-700 flex items-center gap-1.5">
            <Wallet className="w-3.5 h-3.5" /> Planos financeiros do curso
          </p>
          <p className="text-[11px] text-slate-500 mt-0.5">{cg?.name || ''} · curso vinculado</p>
        </div>
        {selectedIds.length > 0 && (
          <div className="text-right shrink-0">
            <p className="text-[10px] uppercase tracking-wider text-slate-500">Total estimado/ano</p>
            <p className="text-base font-bold text-violet-700" data-testid="wiz-fin-total">{fmtBRL(totalEstimated)}</p>
          </div>
        )}
      </div>

      {loading && (
        <div className="flex items-center gap-2 text-sm text-slate-500"><Loader2 className="w-4 h-4 animate-spin" /> Carregando planos…</div>
      )}
      {error && <p className="text-xs text-rose-600">{error}</p>}
      {!loading && plans.length === 0 && (
        <div className="rounded-lg border border-dashed border-slate-300 bg-slate-50 p-4 text-center text-sm text-slate-500" data-testid="wiz-fin-empty">
          Nenhum plano cadastrado para este curso. Cadastre em <em>Financeiro → Planos por Curso</em>.
        </div>
      )}

      <div className="space-y-2">
        {plans.map(p => {
          const checked = selectedIds.includes(p.id);
          const isNew = mode === 'edit' && initialPlansRef.current && !initialPlansRef.current.has(p.id) && checked;
          const total = (Number(p.installment_value) || 0) * (Number(p.num_installments) || 0);
          return (
            <div
              key={p.id}
              role="button"
              tabIndex={0}
              onClick={() => togglePlan(p.id)}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); togglePlan(p.id); } }}
              data-testid={`wiz-fin-plan-${p.id}`}
              className={`w-full text-left rounded-xl border-2 px-3 py-2.5 transition-all cursor-pointer ${
                checked
                  ? 'border-violet-400 bg-violet-50/50 shadow-sm'
                  : 'border-slate-200 bg-white hover:border-violet-200 hover:bg-violet-50/30'
              }`}
            >
              <div className="flex items-start gap-2">
                <div className={`w-4 h-4 rounded border-2 flex items-center justify-center shrink-0 mt-0.5 ${
                  checked ? 'border-violet-600 bg-violet-600' : 'border-slate-300 bg-white'
                }`}>
                  {checked && <CheckCircle2 className="w-3 h-3 text-white" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="text-sm font-semibold text-slate-800">{p.name}</p>
                    <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 font-bold">
                      {PLAN_CATEGORY_LABELS[p.category] || p.category}
                    </span>
                    {isNew && (
                      <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700 font-bold" data-testid={`wiz-fin-plan-new-${p.id}`}>
                        Novo · gerará títulos
                      </span>
                    )}
                    {p.is_default && (
                      <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-blue-100 text-blue-700 font-bold">
                        Padrão
                      </span>
                    )}
                  </div>
                  <div className="grid grid-cols-3 gap-2 mt-1.5 text-[11px]">
                    <div>
                      <span className="text-slate-500 block">Parcelas</span>
                      <span className="font-semibold text-slate-700">{p.num_installments}x</span>
                    </div>
                    <div>
                      <span className="text-slate-500 block">Valor</span>
                      <span className="font-semibold text-slate-700">{fmtBRL(p.installment_value)}</span>
                    </div>
                    <div>
                      <span className="text-slate-500 block">Total</span>
                      <span className="font-semibold text-violet-700">{fmtBRL(total)}</span>
                    </div>
                  </div>
                  {p.first_installment_different_value && (
                    <p className="text-[10.5px] text-amber-700 mt-1">
                      ⚠ 1ª parcela diferenciada: {fmtBRL(p.first_installment_value)}
                    </p>
                  )}
                  {p.scholarship_id && (
                    <p className="text-[10.5px] text-emerald-700 mt-0.5">
                      🎓 Bolsa de desconto vinculada
                    </p>
                  )}
                </div>
                {/* Ações por plano (afetam o curso) */}
                <div className="flex items-center gap-1 shrink-0" onClick={(e) => e.stopPropagation()}>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setEditingPlan({
                        id: p.id,
                        name: p.name,
                        category: p.category,
                        course_id: p.course_id,
                        num_installments: p.num_installments,
                        installment_value: p.installment_value,
                        abatement: p.abatement || 0,
                        due_day: p.due_day || 10,
                        first_installment_different_value: !!p.first_installment_different_value,
                        first_installment_value: p.first_installment_value || '',
                        is_default: !!p.is_default,
                        active: p.active !== false,
                        // restantes herdam do plano original (vão preservados)
                        _full: p,
                      });
                    }}
                    title="Editar plano (afeta todos os alunos do curso)"
                    className="h-7 w-7 inline-flex items-center justify-center rounded-md border border-blue-200 text-blue-600 hover:bg-blue-50"
                    data-testid={`wiz-fin-plan-edit-${p.id}`}
                  >
                    <Edit2 className="w-3.5 h-3.5" />
                  </button>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setDeletePlanConfirm({ plan: p });
                    }}
                    title="Excluir plano"
                    className="h-7 w-7 inline-flex items-center justify-center rounded-md border border-rose-200 text-rose-600 hover:bg-rose-50"
                    data-testid={`wiz-fin-plan-delete-${p.id}`}
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {mode === 'edit' && newSelections.length > 0 && (
        <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 flex items-start gap-2" data-testid="wiz-fin-new-warn">
          <CheckCircle2 className="w-4 h-4 text-emerald-700 mt-0.5 shrink-0" />
          <p className="text-[11.5px] text-emerald-900 leading-snug">
            <strong>{newSelections.length}</strong> plano(s) novo(s) selecionado(s). Ao salvar, títulos serão gerados automaticamente para o aluno.
          </p>
        </div>
      )}

      <p className="text-[10.5px] text-slate-400 leading-snug pt-1">
        💡 Os títulos são gerados quando o aluno é salvo (POST/PUT /students). Para editar valores, vá em Financeiro → Títulos do aluno.
      </p>

      {/* Títulos já existentes — disponível somente em modo edição quando o aluno já foi salvo */}
      {mode === 'edit' && studentId && (
        <FinanceiroTitulosExistentes studentId={studentId} />
      )}

      {/* Dialog: Editar plano (course-level) */}
      <Dialog open={!!editingPlan} onOpenChange={(o) => !o && !editBusy && setEditingPlan(null)}>
        <DialogContent className="max-w-md" data-testid="wiz-fin-plan-edit-dialog">
          <VisuallyHidden.Root><DialogTitle>Editar plano financeiro</DialogTitle></VisuallyHidden.Root>
          {editingPlan && (
            <div className="space-y-3">
              <div>
                <p className="text-sm font-bold text-slate-800">Editar plano financeiro</p>
                <p className="text-[11px] text-amber-700 mt-1">
                  ⚠ Alterações afetam todos os alunos vinculados a este curso. Títulos já gerados não são alterados retroativamente.
                </p>
              </div>
              <div>
                <Label className="text-xs">Nome</Label>
                <Input value={editingPlan.name} onChange={e => setEditingPlan(p => ({ ...p, name: e.target.value }))} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Parcelas</Label>
                  <Input type="number" min="1" value={editingPlan.num_installments}
                    onChange={e => setEditingPlan(p => ({ ...p, num_installments: e.target.value }))} />
                </div>
                <div>
                  <Label className="text-xs">Valor da parcela</Label>
                  <Input type="number" step="0.01" value={editingPlan.installment_value}
                    onChange={e => setEditingPlan(p => ({ ...p, installment_value: e.target.value }))} />
                </div>
                <div>
                  <Label className="text-xs">Abatimento (por parcela)</Label>
                  <Input type="number" step="0.01" value={editingPlan.abatement}
                    onChange={e => setEditingPlan(p => ({ ...p, abatement: e.target.value }))} />
                </div>
                <div>
                  <Label className="text-xs">Dia de vencimento</Label>
                  <Input type="number" min="1" max="31" value={editingPlan.due_day}
                    onChange={e => setEditingPlan(p => ({ ...p, due_day: e.target.value }))} />
                </div>
              </div>
              <label className="flex items-center gap-2 text-xs text-slate-700">
                <input type="checkbox" checked={!!editingPlan.first_installment_different_value}
                  onChange={e => setEditingPlan(p => ({ ...p, first_installment_different_value: e.target.checked }))} />
                1ª parcela com valor diferente
              </label>
              {editingPlan.first_installment_different_value && (
                <div>
                  <Label className="text-xs">Valor da 1ª parcela</Label>
                  <Input type="number" step="0.01" value={editingPlan.first_installment_value}
                    onChange={e => setEditingPlan(p => ({ ...p, first_installment_value: e.target.value }))} />
                </div>
              )}
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={() => setEditingPlan(null)} disabled={editBusy}>Cancelar</Button>
                <Button onClick={savePlanEdit} disabled={editBusy} className="bg-violet-600 hover:bg-violet-700"
                  data-testid="wiz-fin-plan-edit-confirm">
                  {editBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Salvar'}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Dialog: Confirmar exclusão de plano */}
      <Dialog open={!!deletePlanConfirm} onOpenChange={(o) => !o && !deleteBusy && setDeletePlanConfirm(null)}>
        <DialogContent className="max-w-md" data-testid="wiz-fin-plan-delete-dialog">
          <VisuallyHidden.Root><DialogTitle>Excluir plano financeiro</DialogTitle></VisuallyHidden.Root>
          {deletePlanConfirm && (
            <div className="space-y-3">
              <div className="flex items-start gap-2">
                <AlertTriangle className="w-5 h-5 text-rose-600 shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-bold text-slate-800">Excluir plano financeiro?</p>
                  <p className="text-xs text-slate-600 mt-1">
                    <strong>{deletePlanConfirm.plan.name}</strong>
                  </p>
                  <p className="text-[11px] text-rose-700 mt-2">
                    Esta ação remove o plano do curso e desvincula de todos os alunos selecionados. Títulos já gerados continuam existindo (ajuste-os em Financeiro → Títulos).
                  </p>
                </div>
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={() => setDeletePlanConfirm(null)} disabled={deleteBusy}>Cancelar</Button>
                <Button onClick={deletePlan} disabled={deleteBusy} className="bg-rose-600 hover:bg-rose-700"
                  data-testid="wiz-fin-plan-delete-confirm">
                  {deleteBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Excluir'}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ─── Sub-bloco: títulos já existentes do aluno (somente modo edição) ─────
const FinanceiroTitulosExistentes = ({ studentId }) => {
  const [titulos, setTitulos] = useState([]);
  const [loading, setLoading] = useState(true);
  const yearParam = new Date().getFullYear();

  const loadTitulos = useCallback(async () => {
    if (!studentId) return;
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(
        `${API_URL}/api/financial/aluno/${studentId}/dashboard?year=${yearParam}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.ok) {
        const data = await res.json();
        setTitulos(Array.isArray(data?.titulos) ? data.titulos : []);
      } else {
        setTitulos([]);
      }
    } catch {
      setTitulos([]);
    } finally {
      setLoading(false);
    }
  }, [studentId, yearParam]);

  useEffect(() => { loadTitulos(); }, [loadTitulos]);

  const { openAction, isAdmin: admin, dialogNode } = useTituloActions({ onChanged: loadTitulos });

  const fmtDateBR = (d) => {
    if (!d) return '-';
    const m = String(d).slice(0, 10).split('-');
    return m.length === 3 ? `${m[2]}/${m[1]}/${m[0]}` : String(d);
  };

  const STATUS_LABEL = {
    pago: { label: 'Pago', cls: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
    aberto: { label: 'Aberto', cls: 'bg-blue-50 text-blue-700 border-blue-200' },
    pendente: { label: 'Pendente', cls: 'bg-blue-50 text-blue-700 border-blue-200' },
    vencido: { label: 'Vencido', cls: 'bg-rose-50 text-rose-700 border-rose-200' },
    cancelado: { label: 'Cancelado', cls: 'bg-slate-100 text-slate-600 border-slate-200' },
    renegociado: { label: 'Renegociado', cls: 'bg-amber-50 text-amber-700 border-amber-200' },
  };

  return (
    <div className="rounded-xl border border-slate-200 bg-white mt-4 overflow-hidden" data-testid="wiz-fin-titulos-existentes">
      <div className="px-3 py-2 border-b border-slate-100 flex items-center justify-between bg-slate-50">
        <div className="flex items-center gap-2">
          <Receipt className="w-4 h-4 text-violet-500" />
          <span className="text-sm font-semibold text-slate-800">Títulos já gerados ({yearParam})</span>
          <span className="text-[11px] text-slate-500">({titulos.length})</span>
        </div>
        {loading && <Loader2 className="w-3.5 h-3.5 animate-spin text-slate-400" />}
      </div>

      {!loading && titulos.length === 0 ? (
        <div className="p-4 text-center text-xs text-slate-400">Nenhum título encontrado em {yearParam}.</div>
      ) : (
        <div className="divide-y divide-slate-100">
          {titulos.map((t) => {
            const st = String(t.status || '').toLowerCase();
            const isPaid = st === 'pago';
            const isCancelled = st === 'cancelado';
            const editBlocked = isCancelled || (isPaid && !admin);
            const editTitle = isCancelled
              ? 'Título cancelado não pode ser editado. Restaure-o antes.'
              : (isPaid && !admin)
                ? 'Título pago só pode ser editado por administrador. Estorne o pagamento primeiro.'
                : 'Editar título';
            const hasEstornoSnapshot = !!t.estorno_payment_snapshot;
            const badge = STATUS_LABEL[st] || STATUS_LABEL.aberto;
            return (
              <div key={t.id} className="px-3 py-2 flex items-center gap-2 flex-wrap" data-testid={`wiz-fin-titulo-${t.id}`}>
                <div className="flex-1 min-w-[200px]">
                  <p className="text-sm font-semibold text-slate-800 truncate">{t.descricao || t.categoria || '—'}</p>
                  <p className="text-[11px] text-slate-500">
                    {t.competencia && <>Comp. {t.competencia} · </>}
                    Venc. {fmtDateBR(t.vencimento)} · {fmtBRL(t.valor_original)}
                  </p>
                </div>
                <span className={`inline-flex items-center text-[10px] font-semibold px-2 py-0.5 rounded-full border ${badge.cls}`}>
                  {badge.label}
                </span>
                <div className="flex items-center gap-1 ml-auto">
                  <Button size="sm" variant="outline" className="h-7 text-xs"
                    onClick={() => openAction('edit', t)} disabled={editBlocked} title={editTitle}
                    data-testid={`wiz-btn-edit-${t.id}`}>
                    <Edit2 className="w-3 h-3 mr-1" /> Editar
                  </Button>
                  {!isCancelled && !isPaid && (
                    <Button size="sm" variant="outline" className="h-7 text-xs text-rose-700 border-rose-200 hover:bg-rose-50"
                      onClick={() => openAction('cancel', t)}
                      data-testid={`wiz-btn-cancel-${t.id}`}>
                      <Ban className="w-3 h-3 mr-1" /> Cancelar
                    </Button>
                  )}
                  {isCancelled && (
                    <Button size="sm" variant="outline" className="h-7 text-xs text-emerald-700 border-emerald-200 hover:bg-emerald-50"
                      onClick={() => openAction('restore', t)}
                      data-testid={`wiz-btn-restore-${t.id}`}>
                      <RotateCcw className="w-3 h-3 mr-1" /> Restaurar
                    </Button>
                  )}
                  {isPaid && (
                    <Button size="sm" variant="outline" className="h-7 text-xs text-amber-700 border-amber-200 hover:bg-amber-50"
                      onClick={() => openAction('estornar', t)}
                      data-testid={`wiz-btn-estornar-${t.id}`}>
                      <Undo2 className="w-3 h-3 mr-1" /> Estornar
                    </Button>
                  )}
                  {hasEstornoSnapshot && !isPaid && (
                    <Button size="sm" variant="outline" className="h-7 text-xs text-emerald-700 border-emerald-200 hover:bg-emerald-50"
                      onClick={() => openAction('reverter', t)}
                      data-testid={`wiz-btn-reverter-${t.id}`}>
                      <RotateCcw className="w-3 h-3 mr-1" /> Reverter
                    </Button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {dialogNode}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════
//  FASE 4 — Checklist de pendências (Revisão Final)
// ═══════════════════════════════════════════════════════════════════════
// Mapeia chaves de checklist → índice de step do wizard (para "Corrigir agora")
const CHECKLIST_STEP_INDEX = {
  aluno: 0,
  turma: 0,
  responsavel: 1,
  pedagogico: 1,
  contato: 2,
  endereco: 2,
  documentos: 4,
  financeiro: 6,
};

export const buildWizardChecklist = (form, respFin, classGroups) => {
  const cg = classGroups.find(c => c.id === form.class_group_id);
  return [
    { key: 'aluno', label: 'Dados do Aluno', ok: !!form.name?.trim(), warn: !form.birth_date || !form.gender ? 'data nasc/gênero opcional' : null },
    { key: 'turma', label: 'Turma', ok: !!cg, warn: !cg ? 'turma não vinculada (opcional)' : null },
    { key: 'responsavel', label: 'Responsável Financeiro', ok: !!form.responsible_id, fail: !form.responsible_id ? 'obrigatório' : null, warn: respFin && isResponsavelIncomplete(respFin) ? 'cadastro incompleto · boleto bloqueado' : null },
    { key: 'pedagogico', label: 'Responsável Pedagógico', ok: !!(form.responsible_didactic_id || form.responsible_id), warn: !form.responsible_didactic_id ? 'usando o financeiro' : null },
    { key: 'contato', label: 'Contato', ok: !!(form.email || form.phone || form.cellphone), warn: !(form.email || form.phone || form.cellphone) ? 'sem e-mail/telefone' : null },
    { key: 'endereco', label: 'Endereço', ok: !!(form.address && form.city && form.state), warn: !(form.address && form.city && form.state) ? 'incompleto' : null },
    { key: 'documentos', label: 'Documentos', ok: !!(form.cpf || form.rg), warn: !(form.cpf || form.rg) ? 'CPF e RG vazios' : null },
    { key: 'financeiro', label: 'Plano financeiro', ok: (form.financial_plans || []).length > 0, warn: (form.financial_plans || []).length === 0 ? 'nenhum plano vinculado' : null },
  ];
};

export const ChecklistCard = ({ items, onJump }) => {
  const total = items.length;
  const okCount = items.filter(i => i.ok && !i.warn && !i.fail).length;
  const failCount = items.filter(i => i.fail).length;
  const pct = Math.round((okCount / total) * 100);
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-3" data-testid="wiz-checklist">
      <div className="flex items-center justify-between mb-2">
        <p className="text-xs uppercase tracking-wider font-semibold text-slate-700 flex items-center gap-1.5">
          <ListChecks className="w-3.5 h-3.5" /> Checklist do cadastro
        </p>
        <span className={`text-[11px] font-bold ${failCount > 0 ? 'text-rose-600' : pct === 100 ? 'text-emerald-600' : 'text-amber-600'}`}>
          {okCount}/{total} {pct === 100 ? '· tudo certo' : `· ${pct}%`}
        </span>
      </div>
      <div className="h-1.5 rounded-full bg-slate-100 overflow-hidden mb-2">
        <div className={`h-full transition-all ${failCount > 0 ? 'bg-rose-500' : pct === 100 ? 'bg-emerald-500' : 'bg-amber-500'}`} style={{ width: `${pct}%` }} />
      </div>
      <div className="space-y-1">
        {items.map(it => {
          const stepIdx = CHECKLIST_STEP_INDEX[it.key];
          const showFix = (it.fail || it.warn) && onJump && stepIdx !== undefined;
          return (
            <div
              key={it.key}
              className={`flex items-center gap-2 text-[11.5px] rounded-md px-2 py-1 ${
                it.fail ? 'bg-rose-50/60' : it.warn ? 'bg-amber-50/50' : 'bg-transparent'
              }`}
              data-testid={`wiz-checklist-${it.key}`}
            >
              {it.fail ? (
                <AlertCircle className="w-3.5 h-3.5 text-rose-600 shrink-0" />
              ) : it.ok && !it.warn ? (
                <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600 shrink-0" />
              ) : (
                <AlertTriangle className="w-3.5 h-3.5 text-amber-500 shrink-0" />
              )}
              <span className={`flex-1 truncate ${it.fail ? 'text-rose-700' : it.ok && !it.warn ? 'text-slate-700' : 'text-amber-800'}`}>
                {it.label}
                {it.fail && <span className="text-rose-600"> · {it.fail}</span>}
                {!it.fail && it.warn && <span className="text-amber-700"> · {it.warn}</span>}
              </span>
              {showFix && (
                <button
                  type="button"
                  onClick={() => onJump(stepIdx)}
                  className={`shrink-0 text-[10.5px] font-semibold px-2 py-0.5 rounded-md transition-colors ${
                    it.fail
                      ? 'bg-rose-600 hover:bg-rose-700 text-white'
                      : 'bg-amber-100 hover:bg-amber-200 text-amber-800 border border-amber-300'
                  }`}
                  data-testid={`wiz-checklist-fix-${it.key}`}
                >
                  Corrigir agora
                </button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

